import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';
import { isAxiosError } from 'axios';
import type {
  AssignableRole,
  Role,
  UpdateUserBusinessDetailsRequest,
} from '../../api/admin';
import {
  FullWidthYellowPanel,
  SmallGrayPanel,
  SmallGreenPanel,
  backendPalette,
} from '../../components/backend/BackendCardSystem';
import {
  cyberGlassBackdrop,
  cyberGlassHairline,
  cyberGlassSurface,
  cyberGlassTextMuted,
  cyberGlassTextPrimary,
} from '../../styles/cyberGlass';
import { useOnboardingOverview, useOnboardingUserTasks } from '../../hooks/useOnboardingTasks';
import {
  useAgentTitles,
  useDeleteUser,
  useUpdateBan,
  useUpdateClientAssignment,
  useUpdateAgentMeetingPermissions,
  useUpdateAgentTitle,
  useInviteUser,
  useUpdateRole,
  useUpdateUserBusinessDetails,
  useUser,
  USERS_PAGE_SIZE,
  useUsers,
} from '../../hooks/useUsers';
import { usePostCareerJob } from '../../hooks/useCareers';
import { isLocalCandidateId } from '../../lib/careers/registry';
import { useAuth } from '../../context/AuthProvider';
import {
  SCHEDULE_MEETING_TYPES,
  SCHEDULE_MEETING_TYPE_LABELS,
  type ScheduleMeetingType,
} from '../../lib/schedule/meetingTypes';
import GlassPicklist from '../../components/ui/GlassPicklist';
import PortalAvatar from '../../components/profile/PortalAvatar';
import { fetchOrganizationTree } from '../../features/skills-tree/api';
import type { SkillsTreeNode } from '../../features/skills-tree/types';

type DetailsTab = 'details' | 'tasks' | 'projects' | 'tickets' | 'teams' | 'actions';

type CreateUserFormState = {
  email: string;
  role: Role;
  firstName: string;
  lastName: string;
  businessName: string;
};

type CreateUserRole = 'client' | 'agent' | 'admin';

type PostJobFormState = {
  title: string;
  requisitionId: string;
  team: string;
  brainNodeId: string;
  location: string;
  workType: 'full_time' | 'part_time' | 'contract';
  summary: string;
  description: string;
  responsibilities: string;
  requirements: string;
  published: boolean;
};

const DEFAULT_CREATE_USER_FORM: CreateUserFormState = {
  email: '',
  role: 'client',
  firstName: '',
  lastName: '',
  businessName: '',
};

const CREATE_USER_ROLE_OPTIONS: Array<{ value: CreateUserRole; label: string }> = [
  { value: 'client', label: 'Client' },
  { value: 'agent', label: 'Agent' },
  { value: 'admin', label: 'Admin' },
];

const USERS_ROLE_FILTER_OPTIONS: Array<{ value: Role | 'all'; label: string }> = [
  { value: 'all', label: 'All roles' },
  { value: 'admin', label: 'Admin' },
  { value: 'agent', label: 'Agent' },
  { value: 'client', label: 'Client' },
  { value: 'candidate', label: 'Candidate' },
];

const DEFAULT_POST_JOB_FORM: PostJobFormState = {
  title: '',
  requisitionId: '',
  team: '',
  brainNodeId: '',
  location: 'Remote - US',
  workType: 'full_time',
  summary: '',
  description: '',
  responsibilities: '',
  requirements: '',
  published: true,
};

const Wrap = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.2)};
  max-width: 1280px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing(2)};
  min-height: calc(100dvh - 2.5rem);
  // border-radius: 20px;
  // background:
  //   radial-gradient(900px 420px at 0% -10%, rgba(224, 161, 94, 0.26), transparent 58%),
  //   radial-gradient(900px 520px at 110% 0%, rgba(166, 76, 68, 0.2), transparent 62%),
  //   linear-gradient(155deg, #1c1b27 0%, #171622 56%, #111118 100%);

  @media (max-width: 900px) {
    padding: ${({ theme }) => theme.spacing(1.2)};
  }

  @media (max-width: 767px) {
    min-height: calc(100dvh - 5rem);
  }
`;

const Top = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(0.45)};
`;

const Heading = styled.h2`
  margin: 0;
  font-size: clamp(2rem, 4vw, 2.8rem);
  line-height: 1;
  font-weight: 900;
  color: ${backendPalette.yellow};
`;

const Subtitle = styled.p`
  margin: 0;
  color: rgba(245, 245, 245, 0.86);
  font-size: 0.95rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${({ theme }) => theme.spacing(0.8)};
`;

const SmallMetricGreen = styled(SmallGreenPanel)`
  min-height: 102px;
  display: grid;
  align-content: center;
  gap: 6px;
`;

const SmallMetricGray = styled(SmallGrayPanel)`
  min-height: 102px;
  display: grid;
  align-content: center;
  gap: 6px;
`;

const MetricNumber = styled.div`
  position: relative;
  z-index: 1;
  color: ${backendPalette.black};
  font-weight: 900;
  line-height: 1;
  font-size: 2rem;
`;

const MetricNumberRed = styled(MetricNumber)`
  color: ${backendPalette.red};
`;

const MetricLabel = styled.div`
  position: relative;
  z-index: 1;
  color: rgba(26, 26, 26, 0.82);
  font-size: 0.78rem;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  font-weight: 800;
`;

const FilterPanel = styled.section`
  border-radius: 18px;
  background: transparent;
  padding: 14px;
  display: grid;
  gap: ${({ theme }) => theme.spacing(0.8)};
`;

const FilterRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(170px, 230px) auto;
  gap: ${({ theme }) => theme.spacing(0.8)};

  @media (max-width: 860px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  height: 44px;
  border-radius: 12px;
  border: 1px solid rgba(150, 150, 150, 0.58);
  background: transparent;
  color: ${cyberGlassTextPrimary};
  padding: 0 14px;
  font-size: 0.95rem;
  font-weight: 600;

  &::placeholder {
    color: rgba(218, 222, 231, 0.64);
  }

  &:focus,
  &:focus-visible,
  &:active {
    outline: none;
    border: 1px solid rgba(180, 180, 180, 0.92);
    box-shadow: none;
  }
`;

const FilterActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(0.5)};
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const FilterActionButton = styled.button<{ $tone?: 'primary' | 'secondary' }>`
  min-height: 42px;
  border-radius: 12px;
  border: 1px solid rgba(26, 26, 26, 0.34);
  padding: 0 12px;
  background: #e0a15e;
  color: #1a1a1a;
  font-size: 0.8rem;
  font-weight: 900;
  cursor: pointer;
  white-space: nowrap;
`;

const LoadingPanel = styled(SmallGrayPanel)`
  font-weight: 700;
  color: rgba(26, 26, 26, 0.85);
`;

const UsersListSection = styled.section`
  min-height: 0;
  flex: 1;
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  gap: ${({ theme }) => theme.spacing(0.9)};
`;

const UsersListViewport = styled.div`
  min-height: 0;
  overflow-y: auto;
  padding-right: 2px;
`;

const AccordionList = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(0.9)};
  align-content: start;
`;

const AccordionItem = styled(FullWidthYellowPanel)`
  padding: 0;
  overflow: hidden;
`;

const AccordionHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${({ theme }) => theme.spacing(0.9)};
  align-items: center;
  padding: ${({ theme }) => theme.spacing(0.9)};
`;

const IdentityButton = styled.div`
  margin: 0;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.7)};
  text-align: left;
  min-width: 0;
`;

const Avatar = styled(PortalAvatar)`
  flex-shrink: 0;
`;

const IdentityText = styled.div`
  min-width: 0;
  display: grid;
  gap: 2px;
`;

const Name = styled.div`
  font-size: 0.95rem;
  font-weight: 900;
  color: ${backendPalette.black};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Email = styled.div`
  font-size: 0.78rem;
  color: rgba(26, 26, 26, 0.74);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.5)};
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const Badge = styled.span`
  display: inline-block;
  border-radius: 999px;
  border: 1px solid rgba(26, 26, 26, 0.34);
  background: rgba(255, 255, 255, 0.3);
  color: rgba(26, 26, 26, 0.9);
  font-size: 0.69rem;
  line-height: 1;
  font-weight: 800;
  padding: 6px 9px;
  letter-spacing: 0.02em;
`;

const AccordionBody = styled.div`
  border-top: 1px solid rgba(26, 26, 26, 0.24);
  padding: 0 ${({ theme }) => theme.spacing(0.9)} ${({ theme }) => theme.spacing(0.9)};
  display: grid;
  gap: ${({ theme }) => theme.spacing(0.7)};
`;

const BodyContent = styled.div`
  display: grid;
  grid-template-columns: minmax(220px, 300px) minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing(0.95)};
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ContentColumn = styled.div`
  min-width: 0;
  display: grid;
  gap: ${({ theme }) => theme.spacing(0.9)};
`;

const DetailSection = styled.section`
  min-width: 0;
  display: grid;
  gap: ${({ theme }) => theme.spacing(0.55)};
`;

const SectionTitle = styled.div`
  color: rgba(26, 26, 26, 0.74);
  font-size: 0.74rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 900;
`;

const BusinessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: ${({ theme }) => theme.spacing(0.65)} ${({ theme }) => theme.spacing(0.95)};
`;

const DetailRow = styled.div`
  min-width: 0;
  display: grid;
  gap: 2px;
`;

const DetailLabel = styled.div`
  color: rgba(26, 26, 26, 0.74);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-weight: 800;
`;

const DetailValue = styled.div`
  color: ${backendPalette.black};
  font-size: 0.9rem;
  line-height: 1.3;
  font-weight: 800;
`;

const MissingValue = styled.span`
  color: ${backendPalette.red};
`;

const CandidateFileLink = styled.a`
  color: ${backendPalette.black};
  font-size: 0.8rem;
  font-weight: 800;
  text-decoration: underline;
`;

const AccountGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: ${({ theme }) => theme.spacing(0.65)} ${({ theme }) => theme.spacing(0.95)};
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const DetailsButton = styled.button`
  border: 1px solid rgba(26, 26, 26, 0.34);
  border-radius: 999px;
  background: ${backendPalette.black};
  color: ${backendPalette.yellow};
  font-size: 0.78rem;
  font-weight: 900;
  padding: 8px 14px;
  cursor: pointer;
`;

const PaginationPanel = styled.section`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.8)};
  padding: 12px 14px;
  border-radius: 16px;
  border: none;
  background: linear-gradient(180deg, rgba(20, 22, 28, 0.96), rgba(18, 20, 25, 0.96));
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.45);

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const PageInfo = styled.div`
  color: ${cyberGlassTextMuted};
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;

  @media (max-width: 720px) {
    justify-self: start;
  }
`;

const PagerGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(0.55)};

  @media (max-width: 720px) {
    justify-self: center;
  }
`;

const PagerButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 10px;
  min-height: 38px;
  min-width: 102px;
  padding: 0 12px;
  background: rgba(255, 255, 255, 0.04);
  color: ${cyberGlassTextPrimary};
  font-size: 0.74rem;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: rgba(224, 161, 94, 0.55);
    background: rgba(255, 255, 255, 0.07);
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: rgba(224, 161, 94, 0.72);
    box-shadow: 0 0 0 2px rgba(224, 161, 94, 0.14);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const PagePill = styled.span`
  justify-self: end;
  padding: 0;
  color: ${cyberGlassTextPrimary};
  font-weight: 800;
  font-size: 0.74rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;

  @media (max-width: 720px) {
    justify-self: end;
  }
`;

const DrawerBackdrop = styled.button<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  border: 0;
  margin: 0;
  padding: 0;
  background: rgba(0, 0, 0, 0.55);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 180ms ease;
  z-index: 1200;
`;

const Drawer = styled.aside<{ $open: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: clamp(420px, 72vw, 820px);
  height: 100dvh;
  transform: translateX(${({ $open }) => ($open ? '0' : '100%')});
  transition: transform 210ms ease;
  z-index: 1201;
  display: grid;
  grid-template-rows: auto auto 1fr;
  overflow: hidden;
  background: linear-gradient(180deg, rgba(20, 22, 28, 0.96), rgba(18, 20, 25, 0.96));
  color: #e8eaed;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  border-top-left-radius: 18px;
  border-bottom-left-radius: 18px;
  box-shadow: -18px 0 48px rgba(0, 0, 0, 0.45);
  color-scheme: dark;

  @supports not (height: 100dvh) {
    height: 100vh;
  }

  @media (max-width: 860px) {
    width: 100vw;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
`;

const DrawerHeader = styled.div`
  padding: 12px;
  display: grid;
  gap: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const DrawerHeaderBar = styled.div`
  position: relative;
  display: grid;
  gap: 8px;
  padding-right: 96px;
`;

const DrawerIdentity = styled.div`
  min-width: 0;
  display: grid;
  gap: 4px;
`;

const DrawerEyebrow = styled.div`
  color: #9aa4b2;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.18px;
  text-transform: uppercase;
`;

const DrawerTitle = styled.div`
  color: #e8eaed;
  font-size: 1.2rem;
  line-height: 1.1;
  font-weight: 900;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DrawerSub = styled.div`
  color: #9aa4b2;
  font-size: 12px;
  line-height: 1.45;
  font-weight: 700;
`;

const DrawerCloseButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: #cbd2e1;
  display: grid;
  place-items: center;
  font-size: 18px;
  cursor: pointer;
`;

const CloseButton = styled.button`
  border: 1px solid ${cyberGlassHairline};
  border-radius: 999px;
  background: rgba(9, 14, 34, 0.46);
  color: ${cyberGlassTextPrimary};
  font-size: 0.77rem;
  font-weight: 900;
  padding: 7px 12px;
  cursor: pointer;
`;

const Tabs = styled.div`
  display: flex;
  gap: 2px;
  padding: 0 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Tab = styled.button<{ $active: boolean }>`
  min-height: 38px;
  margin-bottom: -1px;
  border-radius: 12px 12px 0 0;
  border: 1px solid
    ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.34)' : 'rgba(255, 255, 255, 0.08)')};
  border-bottom-color: transparent;
  background: ${({ $active }) =>
    $active ? 'rgba(224, 161, 94, 0.1)' : 'rgba(255, 255, 255, 0.02)'};
  color: ${({ $active }) => ($active ? '#f8ebd6' : '#cbd2e1')};
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.02em;
  padding: 0 14px;
  cursor: pointer;
  white-space: nowrap;
  outline: none;

  &:focus,
  &:focus-visible {
    outline: none;
  }
`;

const DrawerBody = styled.div`
  min-height: 0;
  overflow: auto;
  padding: 14px 12px 18px;
  display: grid;
  gap: 18px;
  align-content: start;
`;

const DrawerCard = styled.div`
  display: grid;
  gap: 10px;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
`;

const DrawerGray = styled(DrawerCard)``;
const DrawerGreen = styled(DrawerCard)``;

const DrawerPanelTitle = styled.div`
  color: #9aa4b2;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.18px;
  font-weight: 900;
`;

const DrawerText = styled.div`
  color: rgba(232, 234, 237, 0.94);
  font-size: 13px;
  line-height: 1.45;
  font-weight: 700;
`;

const DrawerInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${({ theme }) => theme.spacing(0.65)} ${({ theme }) => theme.spacing(0.9)};
`;

const DrawerInfoRow = styled.div`
  display: grid;
  gap: 2px;
  min-width: 0;
`;

const DrawerInfoLabel = styled.div`
  color: #9aa4b2;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.18px;
  font-weight: 900;
`;

const DrawerInfoValue = styled.div`
  color: #e8eaed;
  font-size: 13px;
  line-height: 1.4;
  font-weight: 800;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const TaskTitle = styled.div`
  color: #e8eaed;
  font-weight: 900;
  margin-bottom: 4px;
`;

const TaskMeta = styled.div`
  color: rgba(154, 164, 178, 0.96);
  font-size: 12px;
`;

const TaskStatus = styled.span<{ $status: string }>`
  display: inline-block;
  margin-top: 7px;
  width: fit-content;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  padding: 4px 10px;
  font-size: 0.7rem;
  line-height: 1;
  font-weight: 900;
  text-transform: uppercase;
  color: ${({ $status }) =>
    $status === 'completed'
      ? '#d1fae5'
      : $status === 'blocked'
      ? '#fecaca'
      : $status === 'in_progress'
      ? '#fde68a'
      : '#cbd2e1'};
  background: ${({ $status }) =>
    $status === 'completed'
      ? 'rgba(110, 214, 171, 0.12)'
      : $status === 'blocked'
      ? 'rgba(248, 113, 113, 0.14)'
      : $status === 'in_progress'
      ? 'rgba(250, 204, 21, 0.12)'
      : 'rgba(255, 255, 255, 0.04)'};
`;

const ActionLabel = styled.label`
  display: grid;
  gap: 6px;
  color: #e8eaed;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.16px;
  text-transform: uppercase;
`;

const drawerField = css`
  width: 100%;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: #e8eaed;
  outline: none;
  box-shadow: none;
  font-weight: 700;

  &::placeholder {
    color: rgba(154, 164, 178, 0.82);
  }

  &:hover:not(:focus-visible) {
    border-style: dashed;
    border-color: rgba(255, 255, 255, 0.28);
  }

  &:focus-visible {
    background: rgba(255, 255, 255, 0.03);
    border-style: solid;
    border-color: transparent;
    box-shadow: inset 0 0 0 1px rgba(138, 180, 248, 0.75);
    outline: 2px solid rgba(138, 180, 248, 0.35);
    outline-offset: 1px;
  }
`;

const ActionSelect = styled.select`
  ${drawerField}
  min-height: 40px;
  padding: 0 12px;
  font-size: 13px;
  color: #e8eaed;

  option {
    background: #14161c;
    color: #e8eaed;
  }
`;

const ActionButton = styled.button<{ $danger?: boolean }>`
  border-radius: 10px;
  min-height: 40px;
  border: 1px solid rgba(26, 26, 26, 0.34);
  background: ${({ $danger }) => ($danger ? backendPalette.grey : backendPalette.red)};
  color: ${backendPalette.black};
  font-size: 0.8rem;
  font-weight: 900;
  padding: 0 12px;
  cursor: pointer;
  margin-top: 10px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PermissionList = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  gap: ${({ theme }) => theme.spacing(0.45)};
`;

const PermissionRow = styled.label`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.45)};
  color: #e8eaed;
  font-size: 0.82rem;
  font-weight: 800;
  cursor: pointer;
`;

const PermissionCheckbox = styled.input.attrs({ type: 'checkbox' })`
  width: 16px;
  height: 16px;
  margin: 0;
  accent-color: rgba(224, 161, 94, 0.92);
`;

const DetailForm = styled.form`
  display: grid;
  gap: ${({ theme }) => theme.spacing(0.7)};
`;

const DetailField = styled.label`
  display: grid;
  gap: 6px;
  color: #9aa4b2;
  font-size: 10px;
  letter-spacing: 0.18px;
  text-transform: uppercase;
  font-weight: 900;
`;

const DetailInput = styled.input`
  ${drawerField}
  min-height: 40px;
  padding: 10px 12px;
  font-size: 13px;
`;

const DetailTextarea = styled.textarea`
  ${drawerField}
  min-height: 86px;
  resize: vertical;
  padding: 10px 12px;
  font-size: 13px;
  font-family: inherit;
`;

const InlineActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(0.55)};
`;

const InlineMeta = styled.div`
  color: rgba(186, 198, 224, 0.82);
  font-size: 12px;
  font-weight: 800;
`;

const SecondaryButton = styled.button`
  border-radius: 10px;
  min-height: 38px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: #cbd2e1;
  font-size: 0.8rem;
  font-weight: 900;
  padding: 0 12px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DetailSplit = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${({ theme }) => theme.spacing(0.7)};
`;

const ListItem = styled.div`
  color: #e8eaed;
  font-size: 13px;
  line-height: 1.35;
  font-weight: 700;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px dashed rgba(255, 255, 255, 0.18);
  background: transparent;
`;

const HistoryListItem = styled(ListItem)`
  height: 84px;
  min-height: 84px;
  max-height: 84px;
  display: grid;
  grid-template-rows: auto auto auto;
  align-content: center;
  gap: 2px;
  overflow: hidden;
`;

const HistoryRoleLabel = styled.strong`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HistoryMetaLine = styled.div`
  color: rgba(154, 164, 178, 0.96);
  font-size: 11px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HistoryListViewport = styled.div`
  position: relative;
  z-index: 1;
  min-height: 0;
  height: 420px;
  max-height: 420px;
  overflow-y: auto;
  display: grid;
  grid-auto-rows: 84px;
  align-content: start;
  gap: ${({ theme }) => theme.spacing(0.45)};
  padding-right: 4px;
  scrollbar-width: thin;
  scrollbar-color: rgba(26, 26, 26, 0.54) transparent;

  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 999px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(26, 26, 26, 0.52);
    border-radius: 999px;
    border: 2px solid transparent;
    background-clip: padding-box;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(26, 26, 26, 0.64);
    background-clip: padding-box;
  }

  &::-webkit-scrollbar-button {
    display: none;
    width: 0;
    height: 0;
  }
`;

const PanelToolbar = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${({ theme }) => theme.spacing(0.45)};
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing(0.45)};
`;

const PanelSearchInput = styled.input`
  ${drawerField}
  min-height: 38px;
  padding: 0 11px;
  font-size: 12px;
`;

const PanelCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  border-radius: 999px;
  padding: 0 11px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: #cbd2e1;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.16px;
  white-space: nowrap;
`;

const ScrollList = styled.div`
  position: relative;
  z-index: 1;
  max-height: 260px;
  overflow: auto;
  display: grid;
  gap: ${({ theme }) => theme.spacing(0.5)};
  padding-right: 4px;
`;

const TeamBuilderStack = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  gap: ${({ theme }) => theme.spacing(0.55)};
`;

const TeamPathHint = styled.div`
  color: rgba(186, 198, 224, 0.82);
  font-size: 12px;
  line-height: 1.35;
  font-weight: 700;
`;

const TeamPathBadge = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: #cbd2e1;
  font-size: 11px;
  font-weight: 900;
  padding: 0 10px;
`;

const PanelHeadingRow = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(0.45)};
`;

const PlusIconButton = styled.button`
  border: 1px solid rgba(26, 26, 26, 0.34);
  border-radius: 999px;
  width: 28px;
  height: 28px;
  display: inline-grid;
  place-items: center;
  background: ${backendPalette.black};
  color: ${backendPalette.yellow};
  font-size: 1rem;
  line-height: 1;
  font-weight: 900;
  cursor: pointer;
`;

const AssignClientsModalBackdrop = styled.button<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  border: 0;
  margin: 0;
  padding: 0;
  ${cyberGlassBackdrop};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 180ms ease;
  z-index: 92;
`;

const AssignClientsModal = styled.section<{ $open: boolean }>`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(${({ $open }) => ($open ? 1 : 0.98)});
  width: min(860px, calc(100vw - 32px));
  max-height: min(760px, calc(100vh - 48px));
  border-radius: 16px;
  ${cyberGlassSurface};
  border: 1px solid ${cyberGlassHairline};
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.42);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 180ms ease, transform 180ms ease;
  z-index: 93;
  display: grid;
  grid-template-rows: auto 1fr;
`;

const AssignClientsModalHeader = styled.div`
  padding: ${({ theme }) => theme.spacing(0.9)};
  border-bottom: 1px solid ${cyberGlassHairline};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(0.7)};
`;

const AssignClientsModalTitle = styled.div`
  color: ${cyberGlassTextPrimary};
  font-size: 1rem;
  line-height: 1.2;
  font-weight: 900;
`;

const AssignClientsModalSub = styled.div`
  color: ${cyberGlassTextMuted};
  font-size: 0.82rem;
  font-weight: 700;
`;

const AssignClientsModalBody = styled.div`
  padding: ${({ theme }) => theme.spacing(0.9)};
  display: grid;
  gap: ${({ theme }) => theme.spacing(0.7)};
  overflow: auto;
`;

const FormModalBackdrop = styled.button<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  border: 0;
  margin: 0;
  padding: 0;
  ${cyberGlassBackdrop};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 180ms ease;
  z-index: 94;
`;

const FormModal = styled.section<{ $open: boolean }>`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(${({ $open }) => ($open ? 1 : 0.98)});
  width: min(720px, calc(100vw - 28px));
  max-height: min(760px, calc(100vh - 42px));
  border-radius: 16px;
  ${cyberGlassSurface};
  border: 1px solid ${cyberGlassHairline};
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.42);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 180ms ease, transform 180ms ease;
  z-index: 95;
  display: grid;
  grid-template-rows: auto 1fr;
`;

const InviteUserModalBackdrop = styled(FormModalBackdrop)`
  background: rgba(0, 0, 0, 0.55);
`;

const InviteUserModal = styled(FormModal)`
  width: min(760px, calc(100vw - 28px));
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(20, 22, 28, 0.96), rgba(18, 20, 25, 0.96));
  color: #e8eaed;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
  color-scheme: dark;
`;

const InviteUserModalHeader = styled(DrawerHeader)``;
const InviteUserModalHeaderBar = styled(DrawerHeaderBar)`
  padding-right: 56px;
`;
const InviteUserModalIdentity = styled(DrawerIdentity)``;
const InviteUserModalEyebrow = styled(DrawerEyebrow)``;
const InviteUserModalTitle = styled(DrawerTitle)``;
const InviteUserModalSub = styled(DrawerSub)``;
const InviteUserModalCloseButton = styled(DrawerCloseButton)``;
const InviteUserModalBody = styled(DrawerBody)`
  padding: 14px 12px 18px;
`;
const InviteUserModalCard = styled(DrawerGray)`
  gap: 12px;
`;

const FormModalHeader = styled.div`
  padding: ${({ theme }) => theme.spacing(0.9)};
  border-bottom: 1px solid ${cyberGlassHairline};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(0.7)};
`;

const FormModalTitle = styled.div`
  color: ${cyberGlassTextPrimary};
  font-size: 1rem;
  line-height: 1.2;
  font-weight: 900;
`;

const FormModalSub = styled.div`
  color: ${cyberGlassTextMuted};
  font-size: 0.82rem;
  font-weight: 700;
`;

const FormModalBody = styled.div`
  padding: ${({ theme }) => theme.spacing(0.9)};
  overflow: auto;
  display: grid;
  gap: ${({ theme }) => theme.spacing(0.7)};
  align-content: start;
`;

const ModalScrollList = styled(ScrollList)`
  max-height: min(520px, calc(100vh - 260px));
`;

const MetaLine = styled.div`
  color: rgba(154, 164, 178, 0.96);
  font-size: 11px;
  font-weight: 700;
`;

const HelperText = styled.div`
  color: rgba(186, 198, 224, 0.82);
  font-size: 12px;
  line-height: 1.35;
  font-weight: 700;
`;

const CreateUserField = styled(DetailField)`
  color: #9aa4b2;
`;

const CreateUserInput = styled(DetailInput)``;

const CreateUserRolePicker = styled.div`
  position: relative;
`;

const CreateUserRoleButton = styled.button<{ $open: boolean }>`
  ${drawerField}
  width: 100%;
  min-height: 42px;
  border-radius: 10px;
  border-style: ${({ $open }) => ($open ? 'solid' : 'solid')};
  border-color: ${({ $open }) =>
    $open ? 'rgba(138, 180, 248, 0.75)' : 'transparent'};
  background: ${({ $open }) =>
    $open ? 'rgba(255, 255, 255, 0.03)' : 'transparent'};
  padding: 0 36px 0 12px;
  font-size: 13px;
  text-align: left;
  transition: border-color 140ms ease, background-color 140ms ease;

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: rgba(138, 180, 248, 0.75);
    background: rgba(255, 255, 255, 0.03);
    box-shadow: inset 0 0 0 1px rgba(138, 180, 248, 0.75);
  }
`;

const CreateUserRoleChevron = styled.span<{ $open: boolean }>`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%) rotate(${({ $open }) => ($open ? 180 : 0)}deg);
  color: #cbd2e1;
  font-size: 0.78rem;
  line-height: 1;
  pointer-events: none;
  transition: transform 140ms ease;
`;

const CreateUserRoleMenu = styled.div<{
  $top: number;
  $left: number;
  $width: number;
  $openUp: boolean;
}>`
  position: fixed;
  top: ${({ $top }) => `${$top}px`};
  left: ${({ $left }) => `${$left}px`};
  width: ${({ $width }) => `${$width}px`};
  transform: ${({ $openUp }) => ($openUp ? 'translateY(-100%)' : 'none')};
  z-index: 120;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: linear-gradient(180deg, rgba(20, 22, 28, 0.98), rgba(18, 20, 25, 0.98));
  box-shadow: 0 16px 34px rgba(0, 0, 0, 0.45);
  padding: 6px;
  display: grid;
  gap: 4px;
`;

const CreateUserRoleOption = styled.button<{ $selected: boolean }>`
  width: 100%;
  border: 1px solid transparent;
  border-radius: 8px;
  background: ${({ $selected }) => ($selected ? 'rgba(255, 255, 255, 0.08)' : 'transparent')};
  color: #e8eaed;
  padding: 7px 10px;
  text-align: left;
  font-size: 0.84rem;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: rgba(138, 180, 248, 0.45);
  }
`;

const CreateUserHelperText = styled(HelperText)<{ $error?: boolean }>`
  color: ${({ $error }) => ($error ? '#ffb8b2' : 'rgba(186, 198, 224, 0.82)')};
`;

function shortName(email: string): string {
  return email.split('@')[0] || email;
}

function asDisplayText(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

function asTimestamp(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (value instanceof Date) {
    const parsed = value.getTime();
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function roleLabel(value: unknown): string {
  const role = asDisplayText(value)?.toLowerCase();
  if (role === 'admin') return 'ADMIN';
  if (role === 'agent') return 'AGENT';
  if (role === 'client') return 'CLIENT';
  if (role === 'candidate') return 'CANDIDATE';
  return 'USER';
}

function toEditableValue(value: unknown): string {
  return asDisplayText(value) ?? '';
}

function normalizeCustomerType(value: unknown): string | null {
  const text = asDisplayText(value);
  if (!text) return null;
  const lower = text.toLowerCase();
  if (lower === 'business') return 'Business';
  if (lower === 'individual') return 'Individual';
  return text;
}

function formatOrganizationMembershipRole(value: unknown): string | null {
  const text = asDisplayText(value);
  if (!text) return null;
  const lower = text.trim().toLowerCase();
  if (lower === 'owner' || lower === 'primary_account_holder') return 'Primary account holder';
  if (lower === 'member') return 'Member';
  if (lower === 'admin') return 'Admin';
  if (lower === 'agent') return 'Agent';
  if (lower === 'client') return 'Client';
  return text
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function composeIdentityName(profile: Record<string, unknown>, fallbackEmail: string): string {
  const firstName = asDisplayText(profile.firstName);
  const lastName = asDisplayText(profile.lastName);
  const combinedName = `${firstName ?? ''} ${lastName ?? ''}`.trim();
  if (combinedName) return combinedName;

  const direct = asDisplayText(profile.name ?? profile.displayName);
  if (direct) return direct;

  return shortName(fallbackEmail);
}

function getBrainNodeMetaText(node: SkillsTreeNode | null | undefined, key: string): string | null {
  if (!node) return null;
  const meta = (node.meta ?? {}) as Record<string, unknown>;
  return asDisplayText(meta[key]);
}

function getBrainNodeLabel(node: SkillsTreeNode): string {
  return (
    getBrainNodeMetaText(node, 'team') ??
    getBrainNodeMetaText(node, 'designation') ??
    getBrainNodeMetaText(node, 'position') ??
    asDisplayText(node.title) ??
    'Untitled node'
  );
}

function buildBrainNodePath(nodeId: string, nodesById: Map<string, SkillsTreeNode>): SkillsTreeNode[] {
  const path: SkillsTreeNode[] = [];
  const visited = new Set<string>();
  let current = nodesById.get(nodeId) ?? null;

  while (current && !visited.has(current.id)) {
    visited.add(current.id);
    path.unshift(current);
    current = current.parentId ? nodesById.get(current.parentId) ?? null : null;
  }

  return path;
}

function mapSubscriptionStatus(raw: unknown): string | null {
  const value = asDisplayText(raw)?.toLowerCase();
  if (!value) return null;

  if (value === 'active' || value === 'trialing') return 'active';
  if (value === 'canceled' || value === 'cancelled') return 'canceled';
  if (value === 'expired' || value === 'ended' || value === 'paused') return 'expired';
  if (
    value === 'past_due' ||
    value === 'unpaid' ||
    value === 'incomplete' ||
    value === 'incomplete_expired' ||
    value === 'requires_payment_method'
  ) {
    return 'billing issues';
  }
  return value.replace(/_/g, ' ');
}

function mapSubscriptionType(raw: unknown): string | null {
  const value = asDisplayText(raw);
  if (!value) return null;
  const lower = value.toLowerCase();

  if (lower.includes('platform') || lower.includes('pl_')) return 'Platform Development';
  if (lower.includes('marketing') || lower.includes('mm_')) return 'Managed Marketing';
  if (lower.includes('assets') || lower.includes('design') || lower.includes('cybard_club')) return 'Assets/Design';
  return value;
}

function splitMultilineList(value: string): string[] {
  return value
    .split(/\r?\n|;/g)
    .map((item) => item.trim())
    .filter(Boolean);
}

function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export default function UsersPage() {
  const { role: viewerRole, user: authUser } = useAuth();
  const [q, setQ] = useState('');
  const [role, setRole] = useState<Role | 'all'>('all');
  const [page, setPage] = useState(0);
  const [expandedUid, setExpandedUid] = useState<string | null>(null);
  const [detailsUid, setDetailsUid] = useState<string | null>(null);
  const [detailsTab, setDetailsTab] = useState<DetailsTab>('details');
  const [actionsRole, setActionsRole] = useState<AssignableRole>('client');
  const [detailsForm, setDetailsForm] = useState({
    firstName: '',
    lastName: '',
    userType: '',
    contactPhone: '',
    businessName: '',
    businessAddress: '',
    industry: '',
    businessWebsite: '',
    businessPhone: '',
    pointOfContact: '',
    team: '',
    title: '',
    bio: '',
  });
  const [detailsSavedAt, setDetailsSavedAt] = useState<number | null>(null);
  const [clientAssignmentAgentUid, setClientAssignmentAgentUid] = useState('');
  const [assignedClientsQuery, setAssignedClientsQuery] = useState('');
  const [assignPlatformClientsQuery, setAssignPlatformClientsQuery] = useState('');
  const [isAssignClientsModalOpen, setIsAssignClientsModalOpen] = useState(false);
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [isPostJobModalOpen, setIsPostJobModalOpen] = useState(false);
  const [isCreateUserRoleMenuOpen, setIsCreateUserRoleMenuOpen] = useState(false);
  const [createUserRoleMenuPosition, setCreateUserRoleMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 280,
    openUp: false,
  });
  const [createUserForm, setCreateUserForm] = useState<CreateUserFormState>(DEFAULT_CREATE_USER_FORM);
  const [postJobForm, setPostJobForm] = useState<PostJobFormState>(DEFAULT_POST_JOB_FORM);
  const [createUserMessage, setCreateUserMessage] = useState<string | null>(null);
  const [createUserError, setCreateUserError] = useState<string | null>(null);
  const [postJobMessage, setPostJobMessage] = useState<string | null>(null);
  const [postJobError, setPostJobError] = useState<string | null>(null);
  const [roleUpdateError, setRoleUpdateError] = useState<string | null>(null);
  const [deleteUserConfirmText, setDeleteUserConfirmText] = useState('');
  const [deleteUserError, setDeleteUserError] = useState<string | null>(null);
  const [agentTitleDraft, setAgentTitleDraft] = useState('');
  const [newAgentTitle, setNewAgentTitle] = useState('');
  const [agentMeetingPermissionsDraft, setAgentMeetingPermissionsDraft] = useState<
    ScheduleMeetingType[]
  >([]);
  const [teamBuilderNodes, setTeamBuilderNodes] = useState<SkillsTreeNode[]>([]);
  const [teamBuilderPath, setTeamBuilderPath] = useState<string[]>([]);
  const [isLoadingTeamBuilder, setIsLoadingTeamBuilder] = useState(false);
  const [teamBuilderError, setTeamBuilderError] = useState<string | null>(null);
  const [teamBuilderMessage, setTeamBuilderMessage] = useState<string | null>(null);
  const [isSavingTeamBuilder, setIsSavingTeamBuilder] = useState(false);
  const createUserRolePickerRef = useRef<HTMLDivElement | null>(null);
  const createUserRoleButtonRef = useRef<HTMLButtonElement | null>(null);
  const createUserRoleMenuRef = useRef<HTMLDivElement | null>(null);

  const { data: users = [], isLoading } = useUsers({ q, role, page });
  const updateRole = useUpdateRole();
  const deleteUser = useDeleteUser();
  const updateBan = useUpdateBan();
  const updateUserBusinessDetails = useUpdateUserBusinessDetails();
  const updateClientAssignment = useUpdateClientAssignment();
  const updateAgentTitle = useUpdateAgentTitle();
  const updateAgentMeetingPermissions = useUpdateAgentMeetingPermissions();
  const inviteUser = useInviteUser();
  const postCareerJob = usePostCareerJob();

  const expandedUser = useUser(expandedUid);
  const detailsUser = useUser(detailsUid);
  const agentTitles = useAgentTitles(Boolean(detailsUid));

  const onboardingOverview = useOnboardingOverview(undefined, true);
  const onboardingByUid = useMemo(() => {
    const map = new Map<string, { userId: string; completionPercent: number; nextTask: string | null }>();
    for (const user of onboardingOverview.data?.users ?? []) {
      map.set(user.firebaseUid, {
        userId: user.userId,
        completionPercent: user.completionPercent,
        nextTask: user.nextTask,
      });
    }
    return map;
  }, [onboardingOverview.data?.users]);

  const detailsListUser = useMemo(
    () => users.find((user) => user.id === detailsUid) ?? null,
    [users, detailsUid],
  );
  const createUserRoleLabel =
    CREATE_USER_ROLE_OPTIONS.find((option) => option.value === createUserForm.role)?.label ?? 'Client';
  const detailsRole = (detailsUser.data?.role || detailsListUser?.role || 'client') as Role;
  const detailsBanned = Boolean(detailsUser.data?.banned ?? detailsListUser?.banned);
  const detailsOperatorTitle = useMemo(() => {
    const explicitTitle = asDisplayText(
      detailsUser.data?.title ?? detailsUser.data?.agentTitle,
    );
    if (explicitTitle) return explicitTitle;
    if (detailsRole === 'admin') return 'Administrator';
    return null;
  }, [detailsRole, detailsUser.data?.agentTitle, detailsUser.data?.title]);

  const syncCreateUserRoleMenuPosition = useCallback(() => {
    const button = createUserRoleButtonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const estimatedMenuHeight = CREATE_USER_ROLE_OPTIONS.length * 38 + 22;
    const spaceBelow = window.innerHeight - rect.bottom - 12;
    const openUp = spaceBelow < estimatedMenuHeight && rect.top > estimatedMenuHeight;

    setCreateUserRoleMenuPosition({
      top: openUp ? rect.top - 8 : rect.bottom + 8,
      left: rect.left,
      width: rect.width,
      openUp,
    });
  }, []);

  useEffect(() => {
    setActionsRole(
      detailsRole === 'admin' || detailsRole === 'agent' || detailsRole === 'client'
        ? detailsRole
        : 'client',
    );
    setRoleUpdateError(null);
  }, [detailsRole, detailsUid]);

  useEffect(() => {
    if (!isCreateUserRoleMenuOpen) return;
    syncCreateUserRoleMenuPosition();

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      const clickedPicker =
        Boolean(target) && Boolean(createUserRolePickerRef.current?.contains(target as Node));
      const clickedMenu =
        Boolean(target) && Boolean(createUserRoleMenuRef.current?.contains(target as Node));
      if (!clickedPicker && !clickedMenu) {
        setIsCreateUserRoleMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsCreateUserRoleMenuOpen(false);
    };
    const handleViewportShift = () => {
      syncCreateUserRoleMenuPosition();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', handleViewportShift);
    window.addEventListener('scroll', handleViewportShift, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleViewportShift);
      window.removeEventListener('scroll', handleViewportShift, true);
    };
  }, [isCreateUserRoleMenuOpen, syncCreateUserRoleMenuPosition]);

  useEffect(() => {
    const userData = (detailsUser.data ?? {}) as Record<string, unknown>;
    setDetailsForm({
      firstName: toEditableValue(userData.firstName),
      lastName: toEditableValue(userData.lastName),
      userType: toEditableValue(userData.userType).toLowerCase(),
      contactPhone: toEditableValue(userData.contactPhone ?? userData.businessPhone),
      businessName: toEditableValue(userData.businessName),
      businessAddress: toEditableValue(userData.businessAddress),
      industry: toEditableValue(userData.industry),
      businessWebsite: toEditableValue(userData.businessWebsite),
      businessPhone: toEditableValue(userData.businessPhone),
      pointOfContact: toEditableValue(userData.pointOfContact),
      team: toEditableValue(userData.team),
      title: toEditableValue(userData.title ?? userData.agentTitle),
      bio: toEditableValue(userData.bio),
    });
    setClientAssignmentAgentUid('');
    setAssignedClientsQuery('');
    setAssignPlatformClientsQuery('');
    setIsAssignClientsModalOpen(false);
    setAgentTitleDraft(toEditableValue(userData.agentTitle));
    setNewAgentTitle('');
    setAgentMeetingPermissionsDraft(
      ((userData.allowedMeetingTypes ?? []) as string[]).filter((value): value is ScheduleMeetingType =>
        (SCHEDULE_MEETING_TYPES as readonly string[]).includes(value),
      ),
    );
    setDeleteUserConfirmText('');
    setDeleteUserError(null);
    setDetailsSavedAt(null);
  }, [detailsUid, detailsUser.data]);

  const detailsOnboarding = detailsUid ? onboardingByUid.get(detailsUid) : null;
  const detailsTasks = useOnboardingUserTasks(detailsOnboarding?.userId ?? null, Boolean(detailsOnboarding?.userId));

  const stats = {
    total: users.length,
    admins: users.filter((u) => u.role === 'admin').length,
    agents: users.filter((u) => u.role === 'agent').length,
    clients: users.filter((u) => u.role === 'client').length,
    candidates: users.filter((u) => u.role === 'candidate').length,
  };

  const detailsNameSource = (detailsUser.data ?? detailsListUser ?? {}) as Record<string, unknown>;
  const detailsFallbackEmail = detailsUser.data?.email || detailsListUser?.email || 'user@example.com';
  const detailsIdentityName = composeIdentityName(detailsNameSource, detailsFallbackEmail);
  const detailsEmail = detailsUser.data?.email || detailsListUser?.email || 'No email loaded';
  const viewerUid = authUser?.uid ?? null;
  const detailsData = (detailsUser.data ?? {}) as Record<string, unknown>;
  const detailsFirstName = asDisplayText(detailsData.firstName);
  const detailsLastName = asDisplayText(detailsData.lastName);
  const detailsCustomerType = normalizeCustomerType(detailsData.userType);
  const detailsContactPhone = asDisplayText(detailsData.contactPhone ?? detailsData.businessPhone);
  const detailsWorkspaceDisplayName = asDisplayText(detailsData.workspaceDisplayName);
  const detailsWorkspaceRole = formatOrganizationMembershipRole(detailsData.workspaceRole);
  const detailsWorkspaceOrganizationType = normalizeCustomerType(detailsData.workspaceOrganizationType);
  const detailsResolvedSupervisorName = asDisplayText(detailsData.supervisorName);
  const detailsRequisitionId = asDisplayText(detailsData.requisitionId);
  const detailsJobId = asDisplayText(detailsData.jobId);
  const detailsJobTitle = asDisplayText(detailsData.jobTitle);
  const detailsRoleIsClient = detailsRole === 'client';
  const detailsRoleIsCandidate = detailsRole === 'candidate';
  const detailsIsLocalCandidate =
    detailsRoleIsCandidate &&
    Boolean(detailsUid) &&
    (isLocalCandidateId(detailsUid ?? null) || (detailsUid?.startsWith('career_candidate_') ?? false));
  const detailsRoleIsOperator = detailsRole === 'agent' || detailsRole === 'admin';
  const viewerIsAdmin = viewerRole === 'admin';
  const viewerIsAgent = viewerRole === 'agent';
  const isViewingOwnOperatorRecord = Boolean(detailsUid && viewerUid && detailsUid === viewerUid);
  const canEditClientDetails = viewerIsAdmin || viewerIsAgent;
  const canEditOperatorDetails = viewerIsAdmin;
  const canAssignClientToAnyAgent = viewerIsAdmin;
  const canAssignClientToSelf = viewerIsAgent && Boolean(viewerUid);
  const canAssignClientsToSelectedOperator = viewerIsAdmin || (viewerIsAgent && isViewingOwnOperatorRecord);
  const canAdminAssignClientsToOperator = viewerIsAdmin && detailsRole === 'agent';
  const canManageTeamsForSelectedUser = viewerIsAdmin && Boolean(detailsUid) && !detailsIsLocalCandidate;

  const loadTeamBuilderNodes = useCallback(async () => {
    if (!viewerIsAdmin || !detailsUid) return;
    setIsLoadingTeamBuilder(true);
    setTeamBuilderError(null);

    try {
      const tree = await fetchOrganizationTree();
      setTeamBuilderNodes(tree.nodes ?? []);
    } catch (error) {
      setTeamBuilderError(error instanceof Error ? error.message : 'Failed to load Brain nodes.');
    } finally {
      setIsLoadingTeamBuilder(false);
    }
  }, [detailsUid, viewerIsAdmin]);

  useEffect(() => {
    if (!viewerIsAdmin && detailsTab === 'actions') {
      setDetailsTab('details');
    }
  }, [viewerIsAdmin, detailsTab]);

  useEffect(() => {
    if (!detailsUid) return;
    setTeamBuilderPath([]);
    setTeamBuilderError(null);
    setTeamBuilderMessage(null);
  }, [detailsUid]);

  useEffect(() => {
    if (detailsTab !== 'teams' || !viewerIsAdmin || !detailsUid) return;
    void loadTeamBuilderNodes();
  }, [detailsTab, detailsUid, loadTeamBuilderNodes, viewerIsAdmin]);
  const assignedAgents = ((detailsData.assignedAgents ?? []) as Array<Record<string, unknown>>).map(
    (assignment) => ({
      agentUid: asDisplayText(assignment.agentUid) ?? '',
      agentName: asDisplayText(assignment.agentName),
      agentEmail: asDisplayText(assignment.agentEmail) ?? '',
      agentTitle: asDisplayText(assignment.agentTitle),
      assignedAt: asDisplayText(assignment.assignedAt),
    }),
  );
  const assignedAgentUidSet = useMemo(
    () => new Set(assignedAgents.map((assignment) => assignment.agentUid).filter(Boolean)),
    [assignedAgents],
  );
  const isViewerAssignedToClient = Boolean(viewerUid && assignedAgentUidSet.has(viewerUid));
  const assignableAgents = ((detailsData.assignableAgents ?? []) as Array<Record<string, unknown>>).map(
    (agent) => ({
      uid: asDisplayText(agent.uid) ?? '',
      name: asDisplayText(agent.name),
      email: asDisplayText(agent.email) ?? '',
      title: asDisplayText(agent.title),
    }),
  );
  const assignableAgentsNotAssigned = useMemo(
    () =>
      assignableAgents.filter((agent) => {
        if (!agent.uid) return false;
        return !assignedAgentUidSet.has(agent.uid);
      }),
    [assignableAgents, assignedAgentUidSet],
  );
  const assignedClients = ((detailsData.assignedClients ?? []) as Array<Record<string, unknown>>).map(
    (client) => ({
      uid: asDisplayText(client.uid) ?? '',
      name: asDisplayText(client.name),
      email: asDisplayText(client.email) ?? '',
      businessName: asDisplayText(client.businessName),
      assignedAt: asDisplayText(client.assignedAt),
    }),
  );
  const groupedAssignedClients = useMemo(() => {
    const grouped = new Map<
      string,
      {
        key: string;
        businessName: string | null;
        name: string | null;
        email: string;
        assignedAt: string | null;
        clientUids: string[];
      }
    >();

    for (const client of assignedClients) {
      const business = client.businessName?.trim() ?? '';
      const key = business.length > 0 ? `business:${business.toLowerCase()}` : `client:${client.uid}`;
      const existing = grouped.get(key);
      if (existing) {
        if (client.uid && !existing.clientUids.includes(client.uid)) {
          existing.clientUids.push(client.uid);
        }
        continue;
      }

      grouped.set(key, {
        key,
        businessName: client.businessName ?? null,
        name: client.name ?? null,
        email: client.email ?? '',
        assignedAt: client.assignedAt ?? null,
        clientUids: client.uid ? [client.uid] : [],
      });
    }

    return Array.from(grouped.values());
  }, [assignedClients]);
  const assignedClientsQueryLower = assignedClientsQuery.trim().toLowerCase();
  const filteredAssignedClients = useMemo(() => {
    if (!assignedClientsQueryLower) return groupedAssignedClients;
    return groupedAssignedClients.filter((client) => {
      const haystack = `${client.businessName ?? ''} ${client.name ?? ''} ${client.email}`.toLowerCase();
      return haystack.includes(assignedClientsQueryLower);
    });
  }, [groupedAssignedClients, assignedClientsQueryLower]);
  const assignableClients = ((detailsData.assignableClients ?? []) as Array<Record<string, unknown>>).map(
    (client) => ({
      uid: asDisplayText(client.uid) ?? '',
      name: asDisplayText(client.name),
      email: asDisplayText(client.email) ?? '',
      businessName: asDisplayText(client.businessName),
    }),
  );
  const groupedAssignableClients = useMemo(() => {
    const grouped = new Map<
      string,
      {
        key: string;
        businessName: string | null;
        name: string | null;
        email: string;
        clientUid: string;
      }
    >();

    for (const client of assignableClients) {
      const business = client.businessName?.trim() ?? '';
      const key = business.length > 0 ? `business:${business.toLowerCase()}` : `client:${client.uid}`;
      if (grouped.has(key)) {
        continue;
      }
      grouped.set(key, {
        key,
        businessName: client.businessName ?? null,
        name: client.name ?? null,
        email: client.email ?? '',
        clientUid: client.uid,
      });
    }

    return Array.from(grouped.values());
  }, [assignableClients]);
  const assignPlatformClientsQueryLower = assignPlatformClientsQuery.trim().toLowerCase();
  const filteredAssignableClients = useMemo(() => {
    if (!assignPlatformClientsQueryLower) return groupedAssignableClients;
    return groupedAssignableClients.filter((client) => {
      const haystack = `${client.businessName ?? ''} ${client.name ?? ''} ${client.email}`.toLowerCase();
      return haystack.includes(assignPlatformClientsQueryLower);
    });
  }, [groupedAssignableClients, assignPlatformClientsQueryLower]);
  const organizationRoles = ((detailsData.organizationRoles ?? []) as Array<Record<string, unknown>>).map(
    (membership) => ({
      orgId: asDisplayText(membership.orgId) ?? '',
      orgName: asDisplayText(membership.orgName) ?? '--',
      role: formatOrganizationMembershipRole(membership.role) ?? '--',
    }),
  );
  const clientOrganizationMemberships = useMemo(() => {
    const seen = new Set<string>();
    const memberships: Array<{ orgId: string; orgName: string; role: string }> = [];

    if (detailsWorkspaceDisplayName) {
      const role = detailsWorkspaceRole ?? '--';
      const key = `workspace:${detailsWorkspaceDisplayName.toLowerCase()}:${role.toLowerCase()}`;
      seen.add(key);
      memberships.push({
        orgId: asDisplayText(detailsData.workspaceOrgId) ?? '',
        orgName: detailsWorkspaceDisplayName,
        role,
      });
    }

    for (const membership of organizationRoles) {
      const key = `${membership.orgId || membership.orgName}:${membership.role}`.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      memberships.push(membership);
    }

    return memberships;
  }, [detailsData.workspaceOrgId, detailsWorkspaceDisplayName, detailsWorkspaceRole, organizationRoles]);
  const primaryClientOrganization = clientOrganizationMemberships[0] ?? null;
  const clientPurchasedServiceLabels = useMemo(
    () =>
      ((detailsData.purchasedServices ?? []) as Array<Record<string, unknown>>)
        .map((entry) => {
          const label = asDisplayText(entry.label);
          if (label) return label;
          const serviceTitle = asDisplayText(entry.serviceTitle);
          if (!serviceTitle) return null;
          const tierName = asDisplayText(entry.tierName);
          return tierName ? `${serviceTitle} - ${tierName}` : serviceTitle;
        })
        .filter((value): value is string => Boolean(value)),
    [detailsData.purchasedServices],
  );
  const activeTeamBuilderNodes = useMemo(
    () => teamBuilderNodes.filter((node) => node.status !== 'archived'),
    [teamBuilderNodes],
  );
  const jobPostingTeamOptions = useMemo(
    () =>
      activeTeamBuilderNodes
        .map((node) => {
          const team = getBrainNodeMetaText(node, 'team') ?? '';
          if (!team) return null;
          const position = getBrainNodeMetaText(node, 'position') ?? '';
          const teamLabel = team;
          const labelParts = [teamLabel];
          if (position) labelParts.push(position);
          const nodeTitle = node.title.trim();
          if (nodeTitle && nodeTitle !== teamLabel) labelParts.push(nodeTitle);
          return {
            id: node.id,
            teamLabel,
            label: labelParts.join(' / '),
          };
        })
        .filter((option): option is { id: string; teamLabel: string; label: string } => Boolean(option?.teamLabel))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [activeTeamBuilderNodes],
  );
  const jobPostingTeamOptionById = useMemo(
    () => new Map(jobPostingTeamOptions.map((option) => [option.id, option] as const)),
    [jobPostingTeamOptions],
  );
  const teamBuilderNodesById = useMemo(
    () => new Map(activeTeamBuilderNodes.map((node) => [node.id, node])),
    [activeTeamBuilderNodes],
  );
  const teamBuilderChildrenByParent = useMemo(() => {
    const map = new Map<string | null, SkillsTreeNode[]>();
    for (const node of activeTeamBuilderNodes) {
      const parentKey = node.parentId ?? null;
      const siblings = map.get(parentKey) ?? [];
      siblings.push(node);
      map.set(parentKey, siblings);
    }
    for (const siblings of map.values()) {
      siblings.sort((a, b) => getBrainNodeLabel(a).localeCompare(getBrainNodeLabel(b)));
    }
    return map;
  }, [activeTeamBuilderNodes]);
  const assignedTeamNodeId = asDisplayText((detailsData as Record<string, unknown>).teamBrainNodeId) ?? '';
  const assignedTeamNode = useMemo(() => {
    if (!assignedTeamNodeId) return null;
    return teamBuilderNodesById.get(assignedTeamNodeId) ?? null;
  }, [assignedTeamNodeId, teamBuilderNodesById]);
  const assignedTeamPath = useMemo(() => {
    if (!assignedTeamNode) return [] as SkillsTreeNode[];
    return buildBrainNodePath(assignedTeamNode.id, teamBuilderNodesById);
  }, [assignedTeamNode, teamBuilderNodesById]);
  const teamBuilderLevels = useMemo(() => {
    const levels: Array<{
      levelIndex: number;
      selectedNodeId: string;
      parentNode: SkillsTreeNode | null;
      options: SkillsTreeNode[];
    }> = [];

    let parentId: string | null = null;
    let levelIndex = 0;
    while (true) {
      const options = teamBuilderChildrenByParent.get(parentId) ?? [];
      if (!options.length) break;
      const selectedNodeId = teamBuilderPath[levelIndex] ?? '';
      const parentNode = parentId ? teamBuilderNodesById.get(parentId) ?? null : null;
      levels.push({
        levelIndex,
        selectedNodeId,
        parentNode,
        options,
      });
      if (!selectedNodeId || !teamBuilderNodesById.has(selectedNodeId)) break;
      parentId = selectedNodeId;
      levelIndex += 1;
    }

    return levels;
  }, [teamBuilderChildrenByParent, teamBuilderNodesById, teamBuilderPath]);
  const selectedTeamBuilderNode = useMemo(() => {
    const selectedNodeId = teamBuilderPath[teamBuilderPath.length - 1];
    if (!selectedNodeId) return null;
    return teamBuilderNodesById.get(selectedNodeId) ?? null;
  }, [teamBuilderNodesById, teamBuilderPath]);
  const selectedTeamPath = useMemo(() => {
    if (!selectedTeamBuilderNode) return [] as SkillsTreeNode[];
    return buildBrainNodePath(selectedTeamBuilderNode.id, teamBuilderNodesById);
  }, [selectedTeamBuilderNode, teamBuilderNodesById]);
  const selectedTeamBuilderLabel = getBrainNodeMetaText(selectedTeamBuilderNode, 'team');
  const selectedTeamManagerLabel = getBrainNodeMetaText(selectedTeamBuilderNode, 'appointedAgent');

  useEffect(() => {
    if (!activeTeamBuilderNodes.length) {
      setTeamBuilderPath([]);
      return;
    }

    setTeamBuilderPath((previousPath) => {
      const sanitizedPath: string[] = [];
      for (let index = 0; index < previousPath.length; index += 1) {
        const nodeId = previousPath[index];
        const node = teamBuilderNodesById.get(nodeId);
        if (!node) break;
        const expectedParentId = index === 0 ? null : sanitizedPath[index - 1];
        if ((node.parentId ?? null) !== expectedParentId) break;
        sanitizedPath.push(node.id);
      }

      if (sanitizedPath.length > 0) {
        return sanitizedPath;
      }

      if (assignedTeamNode) {
        return buildBrainNodePath(assignedTeamNode.id, teamBuilderNodesById).map((node) => node.id);
      }

      return [];
    });
  }, [activeTeamBuilderNodes.length, assignedTeamNode, teamBuilderNodesById]);

  const openTicketStats = (detailsData.openTicketStats ?? {}) as Record<string, unknown>;
  const statsOpen = Number(openTicketStats.open ?? 0) || 0;
  const statsInProgress = Number(openTicketStats.inProgress ?? 0) || 0;
  const statsWaiting = Number(openTicketStats.blocked ?? 0) || 0;
  const statsTotal = Number(openTicketStats.totalOpen ?? statsOpen + statsInProgress + statsWaiting) || 0;
  const availableAgentTitlesFromUser = ((detailsData.availableAgentTitles ?? []) as unknown[])
    .map((item) => asDisplayText(item))
    .filter((item): item is string => Boolean(item));
  const availableAgentTitles = useMemo(() => {
    const merged = new Map<string, string>();
    for (const title of agentTitles.data ?? []) {
      const normalized = title.trim();
      if (normalized) merged.set(normalized.toLowerCase(), normalized);
    }
    for (const title of availableAgentTitlesFromUser) {
      const normalized = title.trim();
      if (normalized) merged.set(normalized.toLowerCase(), normalized);
    }
    return Array.from(merged.values()).sort((a, b) => a.localeCompare(b));
  }, [agentTitles.data, availableAgentTitlesFromUser]);
  const meetingTypeOptionsFromUser = (
    (detailsData.scheduleMeetingTypeOptions ?? []) as Array<Record<string, unknown>>
  )
    .map((item) => ({
      type: asDisplayText(item.type) as ScheduleMeetingType | null,
      label: asDisplayText(item.label),
    }))
    .filter(
      (item): item is { type: ScheduleMeetingType; label: string } => {
        if (!item.type) return false;
        return (SCHEDULE_MEETING_TYPES as readonly string[]).includes(item.type);
      },
    );
  const meetingTypeOptions = useMemo(() => {
    if (meetingTypeOptionsFromUser.length) return meetingTypeOptionsFromUser;
    return SCHEDULE_MEETING_TYPES.map((type) => ({
      type,
      label: SCHEDULE_MEETING_TYPE_LABELS[type],
    }));
  }, [meetingTypeOptionsFromUser]);
  const initialMeetingPermissions = useMemo(
    () =>
      ((detailsData.allowedMeetingTypes ?? []) as string[]).filter(
        (value): value is ScheduleMeetingType =>
          (SCHEDULE_MEETING_TYPES as readonly string[]).includes(value),
      ),
    [detailsData.allowedMeetingTypes],
  );
  const meetingPermissionsDirty = useMemo(() => {
    const current = [...agentMeetingPermissionsDraft].sort();
    const initial = [...initialMeetingPermissions].sort();
    if (current.length !== initial.length) return true;
    return current.some((value, index) => value !== initial[index]);
  }, [agentMeetingPermissionsDraft, initialMeetingPermissions]);

  const detailsInitialForm = useMemo(() => {
    const userData = (detailsUser.data ?? {}) as Record<string, unknown>;
    return {
      firstName: toEditableValue(userData.firstName),
      lastName: toEditableValue(userData.lastName),
      userType: toEditableValue(userData.userType).toLowerCase(),
      contactPhone: toEditableValue(userData.contactPhone ?? userData.businessPhone),
      businessName: toEditableValue(userData.businessName),
      businessAddress: toEditableValue(userData.businessAddress),
      industry: toEditableValue(userData.industry),
      businessWebsite: toEditableValue(userData.businessWebsite),
      businessPhone: toEditableValue(userData.businessPhone),
      pointOfContact: toEditableValue(userData.pointOfContact),
      team: toEditableValue(userData.team),
      title: toEditableValue(userData.title ?? userData.agentTitle),
      bio: toEditableValue(userData.bio),
    };
  }, [detailsUser.data]);

  const detailsFormDirty =
    detailsForm.firstName !== detailsInitialForm.firstName ||
    detailsForm.lastName !== detailsInitialForm.lastName ||
    detailsForm.userType !== detailsInitialForm.userType ||
    detailsForm.contactPhone !== detailsInitialForm.contactPhone ||
    detailsForm.businessName !== detailsInitialForm.businessName ||
    detailsForm.businessAddress !== detailsInitialForm.businessAddress ||
    detailsForm.industry !== detailsInitialForm.industry ||
    detailsForm.businessWebsite !== detailsInitialForm.businessWebsite ||
    detailsForm.businessPhone !== detailsInitialForm.businessPhone ||
    detailsForm.pointOfContact !== detailsInitialForm.pointOfContact ||
    detailsForm.team !== detailsInitialForm.team ||
    detailsForm.title !== detailsInitialForm.title ||
    detailsForm.bio !== detailsInitialForm.bio;

  const isSavingDetails = updateUserBusinessDetails.isPending;
  const isUpdatingAssignment = updateClientAssignment.isPending;
  const isUpdatingTitle = updateAgentTitle.isPending;
  const isUpdatingMeetingPermissions = updateAgentMeetingPermissions.isPending;

  const handleDetailsFieldChange = (
    field: keyof typeof detailsForm,
    value: string,
  ) => {
    setDetailsForm((prev) => ({
      ...prev,
      [field]: value,
    }));
    setDetailsSavedAt(null);
  };

  const handleDetailsReset = () => {
    setDetailsForm(detailsInitialForm);
    setDetailsSavedAt(null);
  };

  const handleDetailsSave = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const isClientLikeRole = detailsRoleIsClient || detailsRoleIsCandidate;
    const isOperatorRole = detailsRoleIsOperator;
    const canSaveForRole =
      (isClientLikeRole && canEditClientDetails) ||
      (isOperatorRole && canEditOperatorDetails);
    if (
      !detailsUid ||
      (!isClientLikeRole && !isOperatorRole) ||
      !detailsFormDirty ||
      !canSaveForRole
    ) {
      return;
    }

    if (
      detailsRoleIsCandidate &&
      (!detailsForm.firstName.trim() || !detailsForm.lastName.trim() || !detailsForm.userType.trim())
    ) {
      return;
    }

    const normalizedUserType = detailsForm.userType.trim().toLowerCase();
    const payloadUserType: '' | 'business' | 'individual' =
      normalizedUserType === 'business' || normalizedUserType === 'individual'
        ? normalizedUserType
        : '';
    const payload: UpdateUserBusinessDetailsRequest = {};
    if (detailsRoleIsClient) {
      payload.firstName = detailsForm.firstName;
      payload.lastName = detailsForm.lastName;
      payload.userType = payloadUserType;
      payload.contactPhone = detailsForm.contactPhone;
      payload.businessWebsite = detailsForm.businessWebsite;
      payload.businessPhone = detailsForm.businessPhone;
      payload.bio = detailsForm.bio;
    }
    if (detailsRoleIsCandidate) {
      payload.firstName = detailsForm.firstName;
      payload.lastName = detailsForm.lastName;
      payload.userType = payloadUserType;
      payload.contactPhone = detailsForm.contactPhone;
      payload.bio = detailsForm.bio;
    }
    if (isOperatorRole) {
      payload.team = detailsForm.team;
      payload.title = detailsForm.title;
    }

    updateUserBusinessDetails.mutate(
      {
        uid: detailsUid,
        payload,
      },
      {
        onSuccess: () => {
          setDetailsSavedAt(Date.now());
        },
      },
    );
  };

  const handleTeamBuilderLevelChange = (levelIndex: number, nodeId: string) => {
    setTeamBuilderPath((previousPath) => {
      const nextPath = previousPath.slice(0, levelIndex);
      if (nodeId.trim()) {
        nextPath.push(nodeId);
      }
      return nextPath;
    });
    setTeamBuilderError(null);
    setTeamBuilderMessage(null);
  };

  const handleAssignUserToSelectedTeam = async () => {
    if (!canManageTeamsForSelectedUser || !detailsUid || !selectedTeamBuilderNode) {
      return;
    }
    if (!selectedTeamBuilderLabel) {
      setTeamBuilderError('The selected Brain node does not have a team assigned yet.');
      return;
    }

    setIsSavingTeamBuilder(true);
    setTeamBuilderError(null);
    setTeamBuilderMessage(null);

    try {
      await updateUserBusinessDetails.mutateAsync({
        uid: detailsUid,
        payload: {
          team: selectedTeamBuilderLabel,
          teamBrainNodeId: selectedTeamBuilderNode.id,
        },
      });
      await loadTeamBuilderNodes();

      setTeamBuilderMessage(`Assigned team: ${selectedTeamBuilderLabel}.`);
    } catch (error) {
      setTeamBuilderError(error instanceof Error ? error.message : 'Failed to assign team node.');
    } finally {
      setIsSavingTeamBuilder(false);
    }
  };

  const handleAssignClient = () => {
    if (!detailsUid || !detailsRoleIsClient) return;

    if (canAssignClientToAnyAgent) {
      if (!clientAssignmentAgentUid) return;
      updateClientAssignment.mutate({
        uid: detailsUid,
        payload: {
          agentUid: clientAssignmentAgentUid,
          action: 'assign',
        },
      });
      setClientAssignmentAgentUid('');
      return;
    }

    if (canAssignClientToSelf) {
      updateClientAssignment.mutate({
        uid: detailsUid,
        payload: {
          agentUid: viewerUid,
          action: 'assign',
        },
      });
    }
  };

  const handleRemoveClientAssignment = (agentUid: string) => {
    if (!detailsUid || !detailsRoleIsClient || !agentUid) return;

    if (canAssignClientToAnyAgent) {
      updateClientAssignment.mutate({
        uid: detailsUid,
        payload: {
          agentUid,
          action: 'remove',
        },
      });
      return;
    }

    if (canAssignClientToSelf && viewerUid && agentUid === viewerUid) {
      updateClientAssignment.mutate({
        uid: detailsUid,
        payload: {
          agentUid: viewerUid,
          action: 'remove',
        },
      });
    }
  };

  const handleAssignClientToOperator = (clientUid: string) => {
    if (
      !detailsUid ||
      !detailsRoleIsOperator ||
      !clientUid ||
      !canAssignClientsToSelectedOperator
    ) {
      return;
    }

    updateClientAssignment.mutate({
      uid: clientUid,
      payload: {
        agentUid: detailsUid,
        action: 'assign',
      },
    });
  };

  const handleRemoveClientGroupFromOperator = async (clientUids: string[]) => {
    if (!detailsUid || !detailsRoleIsOperator) return;
    if (!canAssignClientsToSelectedOperator) return;

    const uniqueClientUids = Array.from(new Set(clientUids.filter(Boolean)));
    if (!uniqueClientUids.length) return;

    for (const clientUid of uniqueClientUids) {
      // eslint-disable-next-line no-await-in-loop
      await updateClientAssignment.mutateAsync({
        uid: clientUid,
        payload: {
          agentUid: detailsUid,
          action: 'remove',
        },
      });
    }
  };

  const handleUpdateAgentTitle = () => {
    if (!detailsUid || detailsRole !== 'agent' || !viewerIsAdmin) return;
    const nextTitle = (newAgentTitle.trim() || agentTitleDraft.trim()).trim();
    if (!nextTitle) return;

    updateAgentTitle.mutate(
      {
        uid: detailsUid,
        payload: {
          title: nextTitle,
        },
      },
      {
        onSuccess: () => {
          setAgentTitleDraft(nextTitle);
          setNewAgentTitle('');
        },
      },
    );
  };

  const toggleMeetingPermissionDraft = (meetingType: ScheduleMeetingType) => {
    setAgentMeetingPermissionsDraft((prev) =>
      prev.includes(meetingType)
        ? prev.filter((value) => value !== meetingType)
        : [...prev, meetingType],
    );
  };

  const handleSaveMeetingPermissions = () => {
    if (!detailsUid || detailsRole !== 'agent' || !viewerIsAdmin) return;
    updateAgentMeetingPermissions.mutate({
      uid: detailsUid,
      payload: {
        allowedMeetingTypes: agentMeetingPermissionsDraft,
      },
    });
  };

  const handleDeleteUser = () => {
    if (!viewerIsAdmin || !detailsUid) return;
    if (viewerUid && detailsUid === viewerUid) {
      setDeleteUserError('You cannot delete your own account.');
      return;
    }
    if (deleteUserConfirmText.trim().toUpperCase() !== 'DELETE') {
      setDeleteUserError('Type DELETE to confirm account deletion.');
      return;
    }

    setDeleteUserError(null);
    deleteUser.mutate(
      { uid: detailsUid, email: detailsEmail },
      {
        onSuccess: () => {
          setDeleteUserConfirmText('');
          setDeleteUserError(null);
          setDetailsUid(null);
          setExpandedUid(null);
          setDetailsTab('details');
        },
        onError: (error) => {
          if (isAxiosError(error)) {
            const payload = error.response?.data as { message?: string | string[] } | undefined;
            const message = payload?.message;
            if (Array.isArray(message) && message.length > 0) {
              setDeleteUserError(message.join(' '));
              return;
            }
            if (typeof message === 'string' && message.trim().length > 0) {
              setDeleteUserError(message);
              return;
            }
          }
          setDeleteUserError(error instanceof Error ? error.message : 'Failed to delete user.');
        },
      },
    );
  };

  const closeCreateUserModal = () => {
    setIsCreateUserModalOpen(false);
    setIsCreateUserRoleMenuOpen(false);
    setCreateUserError(null);
    setCreateUserMessage(null);
    setCreateUserForm(DEFAULT_CREATE_USER_FORM);
  };

  const closePostJobModal = () => {
    setIsPostJobModalOpen(false);
    setPostJobError(null);
    setPostJobMessage(null);
    setPostJobForm(DEFAULT_POST_JOB_FORM);
  };

  const handleCreateUserSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const email = createUserForm.email.trim();
    const firstName = createUserForm.firstName.trim();
    const lastName = createUserForm.lastName.trim();
    const businessName = createUserForm.businessName.trim();
    if (!isEmail(email)) {
      setCreateUserError('A valid email is required.');
      setCreateUserMessage(null);
      return;
    }
    if (!firstName || !lastName) {
      setCreateUserError('First name and last name are required.');
      setCreateUserMessage(null);
      return;
    }

    setCreateUserError(null);
    setCreateUserMessage(null);

    inviteUser.mutate(
      {
        email,
        role: createUserForm.role as 'admin' | 'agent' | 'client',
        businessName: businessName || undefined,
      },
      {
        onSuccess: async (result) => {
          try {
            await updateUserBusinessDetails.mutateAsync({
              uid: result.uid,
              payload: {
                firstName,
                lastName,
                businessName: businessName || undefined,
              },
            });
            setCreateUserMessage('User invite sent.');
          } catch {
            setCreateUserMessage(
              'User invite sent. Name sync was not confirmed; open Details to verify first and last name.',
            );
          } finally {
            setCreateUserForm(DEFAULT_CREATE_USER_FORM);
          }
        },
        onError: (error) => {
          setCreateUserError(error instanceof Error ? error.message : 'Failed to send invite.');
        },
      },
    );
  };

  const handlePostJobSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const brainNodeId = postJobForm.brainNodeId.trim();
    const team =
      jobPostingTeamOptionById.get(brainNodeId)?.teamLabel ??
      postJobForm.team.trim() ??
      'General';
    if (!postJobForm.title.trim()) {
      setPostJobError('Job title is required.');
      setPostJobMessage(null);
      return;
    }
    if (!postJobForm.requisitionId.trim()) {
      setPostJobError('Requisition number is required.');
      setPostJobMessage(null);
      return;
    }
    if (!brainNodeId) {
      setPostJobError('Select the Brain team node responsible for this role.');
      setPostJobMessage(null);
      return;
    }
    if (!postJobForm.summary.trim()) {
      setPostJobError('Job summary is required.');
      setPostJobMessage(null);
      return;
    }
    if (!postJobForm.description.trim()) {
      setPostJobError('Job description is required.');
      setPostJobMessage(null);
      return;
    }

    setPostJobError(null);
    setPostJobMessage(null);

    postCareerJob.mutate(
      {
        title: postJobForm.title.trim(),
        requisitionId: postJobForm.requisitionId.trim(),
        team,
        brainNodeId,
        location: postJobForm.location.trim() || 'Remote',
        workType: postJobForm.workType,
        summary: postJobForm.summary.trim(),
        description: postJobForm.description.trim(),
        responsibilities: splitMultilineList(postJobForm.responsibilities),
        requirements: splitMultilineList(postJobForm.requirements),
        published: postJobForm.published,
      },
      {
        onSuccess: (result) => {
          setPostJobMessage(
            result.remoteSynced
              ? 'Job posted and synced to backend.'
              : 'Job posted locally and visible on careers page.',
          );
          setPostJobForm(DEFAULT_POST_JOB_FORM);
        },
        onError: (error) => {
          setPostJobError(error instanceof Error ? error.message : 'Failed to post job.');
        },
      },
    );
  };

  return (
    <>
      <Wrap>
        <Top>
          <Heading>CRM</Heading>
          <Subtitle>
            Open any record to inspect client, operator, or candidate context, then launch Details for Tasks, Projects, Tickets, and Actions.
          </Subtitle>
        </Top>

        <StatsGrid>
          <SmallMetricGreen>
            <MetricNumber>{stats.total}</MetricNumber>
            <MetricLabel>Total users</MetricLabel>
          </SmallMetricGreen>
          <SmallMetricGray>
            <MetricNumberRed>{stats.admins}</MetricNumberRed>
            <MetricLabel>Admins</MetricLabel>
          </SmallMetricGray>
          <SmallMetricGreen>
            <MetricNumber>{stats.agents}</MetricNumber>
            <MetricLabel>Agents</MetricLabel>
          </SmallMetricGreen>
          <SmallMetricGray>
            <MetricNumberRed>{stats.clients}</MetricNumberRed>
            <MetricLabel>Clients</MetricLabel>
          </SmallMetricGray>
          <SmallMetricGray>
            <MetricNumberRed>{stats.candidates}</MetricNumberRed>
            <MetricLabel>Candidates</MetricLabel>
          </SmallMetricGray>
        </StatsGrid>

        <FilterPanel>
          <FilterRow>
            <SearchInput
              placeholder="Search by email or name..."
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(0);
              }}
            />
            <GlassPicklist
              value={role}
              options={USERS_ROLE_FILTER_OPTIONS}
              ariaLabel="Filter users by role"
              onChange={(nextRole) => {
                setRole(nextRole);
                setPage(0);
              }}
            />
            <FilterActions>
              <FilterActionButton
                type="button"
                onClick={() => {
                  setIsCreateUserModalOpen(true);
                  setCreateUserError(null);
                  setCreateUserMessage(null);
                }}
              >
                Invite User
              </FilterActionButton>
            </FilterActions>
          </FilterRow>
        </FilterPanel>

        <UsersListSection>
          <UsersListViewport>
            {isLoading ? (
              <LoadingPanel>Loading users...</LoadingPanel>
            ) : (
              <AccordionList>
                {users.map((user) => {
                  const isExpanded = expandedUid === user.id;
                  const profile = isExpanded ? expandedUser.data : null;
                  const userAny = user as unknown as Record<string, unknown>;
                  const profileAny = (profile ?? {}) as Record<string, unknown>;
                  const mergedIdentity = { ...userAny, ...profileAny };
                  const identityName = composeIdentityName(mergedIdentity, user.email);
                  const photo =
                    asDisplayText(
                      profileAny.photoURL ??
                        userAny.photoURL ??
                        userAny.avatarUrl ??
                        userAny.avatarURL ??
                        userAny.avatar ??
                        userAny.imageUrl ??
                        userAny.image,
                    ) ??
                    ((authUser?.email ?? '').toLowerCase() === user.email.toLowerCase()
                      ? asDisplayText(authUser?.photoURL)
                      : null) ??
                    undefined;

                  const firstName = asDisplayText(mergedIdentity.firstName);
                  const lastName = asDisplayText(mergedIdentity.lastName);
                  const customerType = normalizeCustomerType(
                    mergedIdentity.userType ?? mergedIdentity.customerType,
                  );
                  const contactPhone = asDisplayText(
                    mergedIdentity.contactPhone ?? mergedIdentity.phone ?? mergedIdentity.businessPhone,
                  );
                  const requisitionId = asDisplayText(mergedIdentity.requisitionId);
                  const candidateJobId = asDisplayText(mergedIdentity.jobId);
                  const candidateJobTitle = asDisplayText(mergedIdentity.jobTitle);
                  const candidateResumeUrl = asDisplayText(mergedIdentity.resumeDownloadUrl);
                  const candidateCoverLetterUrl = asDisplayText(mergedIdentity.coverLetterDownloadUrl);
                  const crmPointOfContact = asDisplayText(
                    profileAny.pointOfContact ??
                      profileAny.contactName ??
                      `${firstName ?? ''} ${lastName ?? ''}`.trim(),
                  );

                  const businessAddress = asDisplayText(profileAny.businessAddress ?? profileAny.address);
                  const businessWebsite = asDisplayText(profileAny.businessWebsite ?? profileAny.website);
                  const businessPhone = asDisplayText(profileAny.businessPhone ?? contactPhone);
                  const businessPointOfContact = asDisplayText(profileAny.pointOfContact ?? crmPointOfContact);
                  const discordHandle = asDisplayText(profileAny.discordHandle);
                  const portfolioWebsite = asDisplayText(profileAny.portfolioWebsite);
                  const profileBio = asDisplayText(profileAny.bio);
                  const userTeam = asDisplayText(profileAny.team);
                  const supervisorName = asDisplayText(
                    profileAny.supervisorName ?? profileAny.supervisor ?? profileAny.manager,
                  );
                  const candidateCity = asDisplayText(profileAny.candidateCity ?? profileAny.city);
                  const candidateState = asDisplayText(profileAny.candidateState ?? profileAny.state);
                  const candidatePostalCode = asDisplayText(
                    profileAny.candidatePostalCode ?? profileAny.postalCode ?? profileAny.zipCode,
                  );
                  const candidateAddressParts = [
                    businessAddress,
                    [candidateCity, candidateState].filter(Boolean).join(', '),
                    candidatePostalCode,
                  ].filter(Boolean);
                  const candidateAddress =
                    candidateAddressParts.length > 0 ? candidateAddressParts.join(' ') : null;
                  const backgroundCheckAcknowledged = Boolean(profileAny.backgroundCheckAcknowledged);

                  const assignedClientsForAccordion = Array.isArray(profileAny.assignedClients)
                    ? (profileAny.assignedClients as unknown[])
                    : [];
                  const assignedClientCount = assignedClientsForAccordion.length;
                  const organizationRolesForAccordion = Array.isArray(profileAny.organizationRoles)
                    ? (profileAny.organizationRoles as Array<Record<string, unknown>>)
                    : [];
                  const organizationsForAccordion = Array.isArray(profileAny.organizations)
                    ? (profileAny.organizations as Array<Record<string, unknown>>)
                    : [];
                  const clientOrganizations = Array.from(
                    new Set(
                      [
                        ...organizationRolesForAccordion
                          .map((entry) => asDisplayText(entry.orgName))
                          .filter((value): value is string => Boolean(value)),
                        ...organizationsForAccordion
                          .map((entry) => asDisplayText(entry.businessName) ?? asDisplayText(entry.orgName))
                          .filter((value): value is string => Boolean(value)),
                      ]
                        .map((value) => value.trim())
                        .filter((value) => value.length > 0),
                    ),
                  );
                  const clientOrganizationsSummary = clientOrganizations.length > 0
                    ? clientOrganizations.join(', ')
                    : null;
                  const accordionWorkspaceDisplayName =
                    asDisplayText(mergedIdentity.workspaceDisplayName) ?? clientOrganizationsSummary;
                  const accordionWorkspaceRole =
                    formatOrganizationMembershipRole(mergedIdentity.workspaceRole) ??
                    (organizationRolesForAccordion[0]
                      ? formatOrganizationMembershipRole(organizationRolesForAccordion[0].role)
                      : null);
                  const primaryOrganizationRole = organizationRolesForAccordion[0]
                    ? `${asDisplayText(organizationRolesForAccordion[0].orgName) ?? '--'} (${
                        asDisplayText(organizationRolesForAccordion[0].role) ?? '--'
                      })`
                    : null;

                  const stripeSubscriptions = Array.isArray((profileAny.stripe as any)?.subscriptions)
                    ? (((profileAny.stripe as any).subscriptions as unknown[]) ?? [])
                    : [];
                  const primarySubscription = (stripeSubscriptions[0] ?? null) as Record<string, unknown> | null;
                  const subscriptionStatus = mapSubscriptionStatus(
                    profileAny.subscriptionStatus ??
                      profileAny.subscription_state ??
                      primarySubscription?.status ??
                      (profileAny.isPaid ? 'active' : null),
                  );
                  const subscriptionType = mapSubscriptionType(
                    profileAny.subscriptionType ??
                      profileAny.serviceType ??
                      profileAny.plan ??
                      primarySubscription?.priceId,
                  );
                  const purchasedServiceLabels = Array.isArray(profileAny.purchasedServices)
                    ? (profileAny.purchasedServices as Array<Record<string, unknown>>)
                        .map((entry) => {
                          const label = asDisplayText(entry.label);
                          if (label) return label;
                          const serviceTitle = asDisplayText(entry.serviceTitle);
                          if (!serviceTitle) return null;
                          const tierName = asDisplayText(entry.tierName);
                          return tierName ? `${serviceTitle} - ${tierName}` : serviceTitle;
                        })
                        .filter((value): value is string => Boolean(value))
                    : [];
                  const purchasedServiceSummary =
                    purchasedServiceLabels.length > 0
                      ? purchasedServiceLabels.join(' | ')
                      : subscriptionType;
                  const rawHistory = Array.isArray(profileAny.userHistory)
                    ? (profileAny.userHistory as Array<Record<string, unknown>>)
                    : [];
                  const historyRows = rawHistory
                    .map((entry, index) => {
                      const timestamp = asTimestamp(entry.timestamp ?? entry.at ?? entry.date);
                      if (timestamp === null) return null;
                      return {
                        id: asDisplayText(entry.id) ?? `${user.id}-history-${index}`,
                        label: roleLabel(entry.role ?? entry.label),
                        details:
                          asDisplayText(entry.details) ??
                          asDisplayText(entry.source) ??
                          'Role activity',
                        timestamp,
                        isCurrent: Boolean(entry.isCurrent),
                      };
                    })
                    .filter((entry): entry is {
                      id: string;
                      label: string;
                      details: string;
                      timestamp: number;
                      isCurrent: boolean;
                    } => Boolean(entry))
                    .sort((a, b) => b.timestamp - a.timestamp);

                  const fallbackHistoryTimestamp =
                    asTimestamp(profileAny.updatedAt) ??
                    asTimestamp(profileAny.createdAt) ??
                    Date.now();
                  const historyEntries =
                    historyRows.length > 0
                      ? historyRows
                      : [
                          {
                            id: `${user.id}-current-role`,
                            label: roleLabel(user.role),
                            details: 'Current platform role',
                            timestamp: fallbackHistoryTimestamp,
                            isCurrent: true,
                          },
                        ];

                  return (
                    <AccordionItem key={user.id}>
                      <AccordionHeader>
                        <IdentityButton
                          role="button"
                          tabIndex={0}
                          onClick={() => setExpandedUid(isExpanded ? null : user.id)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              setExpandedUid(isExpanded ? null : user.id);
                            }
                          }}
                        >
                          <Avatar
                            uid={user.id}
                            email={user.email}
                            firstName={firstName}
                            lastName={lastName}
                            name={identityName}
                            photoURL={photo}
                            size={44}
                          />
                          <IdentityText>
                            <Name>{identityName}</Name>
                            <Email>{user.email}</Email>
                          </IdentityText>
                        </IdentityButton>

                        <HeaderRight>
                          <Badge>{user.role.toUpperCase()}</Badge>
                        </HeaderRight>
                      </AccordionHeader>

                      {isExpanded ? (
                        <AccordionBody>
                          <BodyContent>
                            <ContentColumn>
                              <DetailSection>
                                <SectionTitle>User history</SectionTitle>
                                <HistoryListViewport>
                                  {historyEntries.map((entry) => (
                                    <HistoryListItem key={entry.id}>
                                      <HistoryRoleLabel>{entry.label}</HistoryRoleLabel>
                                      <HistoryMetaLine>
                                        {entry.isCurrent ? 'Current' : 'History'} - {entry.details}
                                      </HistoryMetaLine>
                                      <HistoryMetaLine>
                                        {new Date(entry.timestamp).toLocaleString()}
                                      </HistoryMetaLine>
                                    </HistoryListItem>
                                  ))}
                                </HistoryListViewport>
                              </DetailSection>
                            </ContentColumn>

                            <ContentColumn>
                              {user.role === 'candidate' ? (
                                <DetailSection>
                                  <SectionTitle>Candidate details</SectionTitle>
                                  <BusinessGrid>
                                    <DetailRow>
                                      <DetailLabel>First name</DetailLabel>
                                      <DetailValue>{firstName || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Last name</DetailLabel>
                                      <DetailValue>{lastName || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Candidate type</DetailLabel>
                                      <DetailValue>{customerType || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Phone number</DetailLabel>
                                      <DetailValue>{contactPhone || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Address</DetailLabel>
                                      <DetailValue>{candidateAddress || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Referred by</DetailLabel>
                                      <DetailValue>{businessPointOfContact || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Background check</DetailLabel>
                                      <DetailValue>
                                        {backgroundCheckAcknowledged ? 'Acknowledged' : <MissingValue>Not acknowledged</MissingValue>}
                                      </DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Requisition #</DetailLabel>
                                      <DetailValue>{requisitionId || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Job ID</DetailLabel>
                                      <DetailValue>{candidateJobId || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Applied role</DetailLabel>
                                      <DetailValue>{candidateJobTitle || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Resume</DetailLabel>
                                      <DetailValue>
                                        {candidateResumeUrl ? (
                                          <CandidateFileLink
                                            href={candidateResumeUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                          >
                                            Open resume
                                          </CandidateFileLink>
                                        ) : (
                                          <MissingValue>--</MissingValue>
                                        )}
                                      </DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Cover letter</DetailLabel>
                                      <DetailValue>
                                        {candidateCoverLetterUrl ? (
                                          <CandidateFileLink
                                            href={candidateCoverLetterUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                          >
                                            Open cover letter
                                          </CandidateFileLink>
                                        ) : (
                                          <MissingValue>--</MissingValue>
                                        )}
                                      </DetailValue>
                                    </DetailRow>
                                  </BusinessGrid>
                                </DetailSection>
                              ) : user.role === 'client' ? (
                                <DetailSection>
                                  <SectionTitle>Client details</SectionTitle>
                                  <BusinessGrid>
                                    <DetailRow>
                                      <DetailLabel>First name</DetailLabel>
                                      <DetailValue>{firstName || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Last name</DetailLabel>
                                      <DetailValue>{lastName || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Business organization</DetailLabel>
                                      <DetailValue>
                                        {accordionWorkspaceDisplayName || <MissingValue>--</MissingValue>}
                                      </DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Organization role</DetailLabel>
                                      <DetailValue>
                                        {accordionWorkspaceRole || <MissingValue>--</MissingValue>}
                                      </DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Phone number</DetailLabel>
                                      <DetailValue>{businessPhone || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Website</DetailLabel>
                                      <DetailValue>{businessWebsite || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                  </BusinessGrid>
                                </DetailSection>
                              ) : user.role === 'agent' ? (
                                <DetailSection>
                                  <SectionTitle>Agent details</SectionTitle>
                                  <BusinessGrid>
                                    <DetailRow>
                                      <DetailLabel>First name</DetailLabel>
                                      <DetailValue>{firstName || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Last name</DetailLabel>
                                      <DetailValue>{lastName || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Title</DetailLabel>
                                      <DetailValue>
                                        {asDisplayText(profileAny.title ?? profileAny.agentTitle) || (
                                          <MissingValue>--</MissingValue>
                                        )}
                                      </DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Team</DetailLabel>
                                      <DetailValue>{userTeam || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Supervisor</DetailLabel>
                                      <DetailValue>{supervisorName || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Contact phone</DetailLabel>
                                      <DetailValue>{contactPhone || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Discord handle</DetailLabel>
                                      <DetailValue>{discordHandle || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Portfolio website</DetailLabel>
                                      <DetailValue>{portfolioWebsite || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Assigned clients</DetailLabel>
                                      <DetailValue>{String(assignedClientCount)}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Bio</DetailLabel>
                                      <DetailValue>{profileBio || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                  </BusinessGrid>
                                </DetailSection>
                              ) : (
                                <DetailSection>
                                  <SectionTitle>Admin details</SectionTitle>
                                  <BusinessGrid>
                                    <DetailRow>
                                      <DetailLabel>First name</DetailLabel>
                                      <DetailValue>{firstName || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Last name</DetailLabel>
                                      <DetailValue>{lastName || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Title</DetailLabel>
                                      <DetailValue>
                                        {asDisplayText(profileAny.title ?? profileAny.agentTitle) || (
                                          <MissingValue>--</MissingValue>
                                        )}
                                      </DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Team</DetailLabel>
                                      <DetailValue>{userTeam || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Supervisor</DetailLabel>
                                      <DetailValue>{supervisorName || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Organization role</DetailLabel>
                                      <DetailValue>{primaryOrganizationRole || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Contact phone</DetailLabel>
                                      <DetailValue>{contactPhone || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Discord handle</DetailLabel>
                                      <DetailValue>{discordHandle || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Portfolio website</DetailLabel>
                                      <DetailValue>{portfolioWebsite || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Bio</DetailLabel>
                                      <DetailValue>{profileBio || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                  </BusinessGrid>
                                </DetailSection>
                              )}

                              {user.role === 'client' ? (
                                <DetailSection>
                                  <SectionTitle>Account health</SectionTitle>
                                  <AccountGrid>
                                    <DetailRow>
                                      <DetailLabel>Subscription status</DetailLabel>
                                      <DetailValue>{subscriptionStatus || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                    <DetailRow>
                                      <DetailLabel>Purchased service + tier</DetailLabel>
                                      <DetailValue>{purchasedServiceSummary || <MissingValue>--</MissingValue>}</DetailValue>
                                    </DetailRow>
                                  </AccountGrid>
                                </DetailSection>
                              ) : null}
                            </ContentColumn>
                          </BodyContent>

                          <FooterActions>
                            <DetailsButton
                              type="button"
                              onClick={() => {
                                setDetailsUid(user.id);
                                setDetailsTab('details');
                              }}
                            >
                              Open Details
                            </DetailsButton>
                          </FooterActions>
                        </AccordionBody>
                      ) : null}
                    </AccordionItem>
                  );
                })}
              </AccordionList>
            )}
          </UsersListViewport>

          <PaginationPanel>
            <PageInfo>Showing page {page + 1} of results</PageInfo>
            <PagerGroup>
              <PagerButton type="button" disabled={page === 0} onClick={() => setPage((value) => value - 1)}>
                Previous
              </PagerButton>
              <PagerButton
                type="button"
                disabled={users.length < USERS_PAGE_SIZE}
                onClick={() => setPage((value) => value + 1)}
              >
                Next
              </PagerButton>
            </PagerGroup>
            <PagePill>Page {page + 1}</PagePill>
          </PaginationPanel>
        </UsersListSection>
      </Wrap>

      <DrawerBackdrop
        type="button"
        aria-label="Close details panel"
        $open={Boolean(detailsUid)}
        onClick={() => setDetailsUid(null)}
      />

      <Drawer $open={Boolean(detailsUid)}>
        <DrawerHeader>
          <DrawerHeaderBar>
            <DrawerIdentity>
              <DrawerEyebrow>CRM profile</DrawerEyebrow>
              <DrawerTitle>{detailsIdentityName}</DrawerTitle>
              <DrawerSub>{detailsEmail}</DrawerSub>
            </DrawerIdentity>
            <DrawerCloseButton type="button" onClick={() => setDetailsUid(null)} aria-label="Close details">
              x
            </DrawerCloseButton>
          </DrawerHeaderBar>
        </DrawerHeader>

        <Tabs>
          <Tab type="button" $active={detailsTab === 'details'} onClick={() => setDetailsTab('details')}>
            Details
          </Tab>
          <Tab type="button" $active={detailsTab === 'tasks'} onClick={() => setDetailsTab('tasks')}>
            Tasks
          </Tab>
          <Tab type="button" $active={detailsTab === 'projects'} onClick={() => setDetailsTab('projects')}>
            Projects
          </Tab>
          <Tab type="button" $active={detailsTab === 'tickets'} onClick={() => setDetailsTab('tickets')}>
            Tickets
          </Tab>
          <Tab type="button" $active={detailsTab === 'teams'} onClick={() => setDetailsTab('teams')}>
            Teams
          </Tab>
          {viewerIsAdmin ? (
            <Tab type="button" $active={detailsTab === 'actions'} onClick={() => setDetailsTab('actions')}>
              Actions
            </Tab>
          ) : null}
        </Tabs>

        <DrawerBody>
          {detailsTab === 'details' ? (
            <>
              {detailsRoleIsClient ? (
                <>
                  <DrawerGray>
                    <DrawerPanelTitle>Client profile</DrawerPanelTitle>
                    <DrawerInfoGrid>
                      <DrawerInfoRow>
                        <DrawerInfoLabel>First name</DrawerInfoLabel>
                        <DrawerInfoValue>{detailsFirstName || <MissingValue>--</MissingValue>}</DrawerInfoValue>
                      </DrawerInfoRow>
                      <DrawerInfoRow>
                        <DrawerInfoLabel>Last name</DrawerInfoLabel>
                        <DrawerInfoValue>{detailsLastName || <MissingValue>--</MissingValue>}</DrawerInfoValue>
                      </DrawerInfoRow>
                      <DrawerInfoRow>
                        <DrawerInfoLabel>Email</DrawerInfoLabel>
                        <DrawerInfoValue>{detailsEmail || <MissingValue>--</MissingValue>}</DrawerInfoValue>
                      </DrawerInfoRow>
                      <DrawerInfoRow>
                        <DrawerInfoLabel>Customer type</DrawerInfoLabel>
                        <DrawerInfoValue>{detailsCustomerType || <MissingValue>--</MissingValue>}</DrawerInfoValue>
                      </DrawerInfoRow>
                      <DrawerInfoRow>
                        <DrawerInfoLabel>Contact phone</DrawerInfoLabel>
                        <DrawerInfoValue>{detailsContactPhone || <MissingValue>--</MissingValue>}</DrawerInfoValue>
                      </DrawerInfoRow>
                    </DrawerInfoGrid>
                  </DrawerGray>

                  <DrawerGray>
                    <DrawerPanelTitle>Organization membership</DrawerPanelTitle>
                    <DrawerInfoGrid>
                      <DrawerInfoRow>
                        <DrawerInfoLabel>Organization</DrawerInfoLabel>
                        <DrawerInfoValue>
                          {primaryClientOrganization?.orgName || <MissingValue>--</MissingValue>}
                        </DrawerInfoValue>
                      </DrawerInfoRow>
                      <DrawerInfoRow>
                        <DrawerInfoLabel>Organization role</DrawerInfoLabel>
                        <DrawerInfoValue>
                          {primaryClientOrganization?.role || <MissingValue>--</MissingValue>}
                        </DrawerInfoValue>
                      </DrawerInfoRow>
                      <DrawerInfoRow>
                        <DrawerInfoLabel>Organization type</DrawerInfoLabel>
                        <DrawerInfoValue>
                          {detailsWorkspaceOrganizationType || <MissingValue>--</MissingValue>}
                        </DrawerInfoValue>
                      </DrawerInfoRow>
                      <DrawerInfoRow>
                        <DrawerInfoLabel>Active services</DrawerInfoLabel>
                        <DrawerInfoValue>
                          {clientPurchasedServiceLabels.length
                            ? clientPurchasedServiceLabels.join(' | ')
                            : <MissingValue>--</MissingValue>}
                        </DrawerInfoValue>
                      </DrawerInfoRow>
                    </DrawerInfoGrid>
                    {clientOrganizationMemberships.length > 1 ? (
                      <>
                        <DrawerText>Organization memberships</DrawerText>
                        {clientOrganizationMemberships.map((membership) => (
                          <ListItem key={`${membership.orgId || membership.orgName}-${membership.role}`}>
                            <strong>{membership.orgName}</strong>
                            <MetaLine>{membership.role}</MetaLine>
                          </ListItem>
                        ))}
                      </>
                    ) : null}
                    <DrawerText>
                      Organization membership is sourced from the current workspace membership model, not legacy client business fields.
                    </DrawerText>
                  </DrawerGray>

                  <DrawerGreen>
                    <DetailForm onSubmit={handleDetailsSave}>
                      <DrawerPanelTitle>Editable client record</DrawerPanelTitle>
                      <DrawerText>
                        These fields save on the client user record. Organization membership and service scope are managed separately.
                      </DrawerText>

                      <DetailSplit>
                        <DetailField>
                          First name
                          <DetailInput
                            value={detailsForm.firstName}
                            onChange={(e) => handleDetailsFieldChange('firstName', e.target.value)}
                            placeholder="First name"
                          />
                        </DetailField>

                        <DetailField>
                          Last name
                          <DetailInput
                            value={detailsForm.lastName}
                            onChange={(e) =>
                              handleDetailsFieldChange('lastName', e.target.value)
                            }
                            placeholder="Last name"
                          />
                        </DetailField>
                      </DetailSplit>

                      <DetailSplit>
                        <DetailField>
                          Customer type
                          <ActionSelect
                            value={detailsForm.userType}
                            onChange={(e) => handleDetailsFieldChange('userType', e.target.value)}
                          >
                            <option value="">Select customer type</option>
                            <option value="individual">Individual</option>
                            <option value="business">Business</option>
                          </ActionSelect>
                        </DetailField>

                        <DetailField>
                          Contact phone
                          <DetailInput
                            value={detailsForm.contactPhone}
                            onChange={(e) =>
                              handleDetailsFieldChange('contactPhone', e.target.value)
                            }
                            placeholder="+1"
                          />
                        </DetailField>
                      </DetailSplit>

                      <DetailSplit>
                        <DetailField>
                          Website
                          <DetailInput
                            value={detailsForm.businessWebsite}
                            onChange={(e) =>
                              handleDetailsFieldChange('businessWebsite', e.target.value)
                            }
                            placeholder="https://"
                          />
                        </DetailField>

                        <DetailField>
                          Business phone
                          <DetailInput
                            value={detailsForm.businessPhone}
                            onChange={(e) =>
                              handleDetailsFieldChange('businessPhone', e.target.value)
                            }
                            placeholder="+1"
                          />
                        </DetailField>
                      </DetailSplit>

                      <DetailField>
                        Notes
                        <DetailTextarea
                          value={detailsForm.bio}
                          onChange={(e) => handleDetailsFieldChange('bio', e.target.value)}
                          placeholder="Client notes"
                        />
                      </DetailField>

                      <InlineActions>
                        {updateUserBusinessDetails.isError ? (
                          <InlineMeta>Failed to save details.</InlineMeta>
                        ) : detailsSavedAt ? (
                          <InlineMeta>Saved</InlineMeta>
                        ) : null}
                        <SecondaryButton
                          type="button"
                          disabled={!detailsFormDirty || isSavingDetails || !canEditClientDetails}
                          onClick={handleDetailsReset}
                        >
                          Reset
                        </SecondaryButton>
                        <ActionButton
                          type="submit"
                          disabled={!detailsFormDirty || isSavingDetails || !canEditClientDetails}
                        >
                          {isSavingDetails ? 'Saving...' : 'Save details'}
                        </ActionButton>
                      </InlineActions>
                    </DetailForm>
                  </DrawerGreen>

                  <DrawerGray>
                    <DrawerPanelTitle>Client assignment</DrawerPanelTitle>
                    {assignedAgents.length ? (
                      assignedAgents.map((assignment) => {
                        const canRemoveThisAgent =
                          canAssignClientToAnyAgent ||
                          (canAssignClientToSelf && Boolean(viewerUid) && assignment.agentUid === viewerUid);
                        return (
                          <ListItem key={`client-assignment-${assignment.agentUid}`}>
                            <strong>
                              {assignment.agentName || assignment.agentEmail}
                              {assignment.agentTitle ? ` (${assignment.agentTitle})` : ''}
                            </strong>
                            <MetaLine>{assignment.agentEmail}</MetaLine>
                            {canRemoveThisAgent ? (
                              <ActionButton
                                type="button"
                                disabled={isUpdatingAssignment}
                                onClick={() => handleRemoveClientAssignment(assignment.agentUid)}
                              >
                                {isUpdatingAssignment ? 'Removing...' : 'Remove agent'}
                              </ActionButton>
                            ) : null}
                          </ListItem>
                        );
                      })
                    ) : (
                      <HelperText>Currently unassigned.</HelperText>
                    )}
                    {canAssignClientToAnyAgent ? (
                      <>
                        <ActionLabel>
                          Add agent to client
                          <ActionSelect
                            value={clientAssignmentAgentUid}
                            onChange={(e) => setClientAssignmentAgentUid(e.target.value)}
                          >
                            <option value="">Select agent</option>
                            {assignableAgentsNotAssigned.map((agent) => (
                              <option key={agent.uid} value={agent.uid}>
                                {(agent.name || agent.email) + (agent.title ? ` (${agent.title})` : '')}
                              </option>
                            ))}
                          </ActionSelect>
                        </ActionLabel>
                        <ActionButton
                          type="button"
                          disabled={!clientAssignmentAgentUid || isUpdatingAssignment}
                          onClick={handleAssignClient}
                        >
                          {isUpdatingAssignment ? 'Saving assignment...' : 'Add agent'}
                        </ActionButton>
                      </>
                    ) : canAssignClientToSelf ? (
                      !isViewerAssignedToClient ? (
                        <ActionButton
                          type="button"
                          disabled={isUpdatingAssignment}
                          onClick={handleAssignClient}
                        >
                          {isUpdatingAssignment ? 'Assigning...' : 'Assign client to me'}
                        </ActionButton>
                      ) : (
                        <HelperText>
                          You are already assigned to this client. Remove your assignment row if needed.
                        </HelperText>
                      )
                    ) : (
                      <HelperText>
                        You do not have permission to change client assignment.
                      </HelperText>
                    )}
                  </DrawerGray>
                </>
              ) : detailsRoleIsCandidate ? (
                <>
                  <DrawerGray>
                    <DrawerPanelTitle>
                      Candidate profile
                    </DrawerPanelTitle>
                    <DrawerText>
                      Capture required candidate information and enrich optional profile details for hiring review.
                    </DrawerText>
                  </DrawerGray>

                  <DrawerGreen>
                    <DetailForm onSubmit={handleDetailsSave}>
                      <DetailSplit>
                        <DetailField>
                          First name
                          <DetailInput
                            value={detailsForm.firstName}
                            onChange={(e) => handleDetailsFieldChange('firstName', e.target.value)}
                            placeholder="First name"
                          />
                        </DetailField>

                        <DetailField>
                          Last name
                          <DetailInput
                            value={detailsForm.lastName}
                            onChange={(e) => handleDetailsFieldChange('lastName', e.target.value)}
                            placeholder="Last name"
                          />
                        </DetailField>
                      </DetailSplit>

                      <DetailSplit>
                        <DetailField>
                          Email
                          <DetailInput value={detailsEmail === 'No email loaded' ? '' : detailsEmail} readOnly />
                        </DetailField>

                        <DetailField>
                          Customer type
                          <ActionSelect
                            value={detailsForm.userType}
                            onChange={(e) => handleDetailsFieldChange('userType', e.target.value)}
                          >
                            <option value="">Select customer type</option>
                            <option value="individual">Individual</option>
                            <option value="business">Business</option>
                          </ActionSelect>
                        </DetailField>
                      </DetailSplit>

                      {detailsRoleIsCandidate ? (
                        <DetailSplit>
                          <DetailField>
                            Requisition #
                            <DetailInput value={detailsRequisitionId ?? ''} readOnly />
                          </DetailField>

                          <DetailField>
                            Job ID
                            <DetailInput value={detailsJobId ?? ''} readOnly />
                          </DetailField>
                        </DetailSplit>
                      ) : null}

                      {detailsRoleIsCandidate ? (
                        <DetailField>
                          Applied role
                          <DetailInput value={detailsJobTitle ?? ''} readOnly />
                        </DetailField>
                      ) : null}

                      <DetailField>
                        Contact phone (optional)
                        <DetailInput
                          value={detailsForm.contactPhone}
                          onChange={(e) => handleDetailsFieldChange('contactPhone', e.target.value)}
                          placeholder="+1"
                        />
                      </DetailField>

                      <DetailSplit>
                        <DetailField>
                          Business name (optional)
                          <DetailInput
                            value={detailsForm.businessName}
                            onChange={(e) => handleDetailsFieldChange('businessName', e.target.value)}
                            placeholder="Business name"
                          />
                        </DetailField>

                        <DetailField>
                          Website (optional)
                          <DetailInput
                            value={detailsForm.businessWebsite}
                            onChange={(e) => handleDetailsFieldChange('businessWebsite', e.target.value)}
                            placeholder="https://"
                          />
                        </DetailField>
                      </DetailSplit>

                      <DetailSplit>
                        <DetailField>
                          Industry (optional)
                          <DetailInput
                            value={detailsForm.industry}
                            onChange={(e) => handleDetailsFieldChange('industry', e.target.value)}
                            placeholder="Industry"
                          />
                        </DetailField>

                        <DetailField>
                          Point of contact (optional)
                          <DetailInput
                            value={detailsForm.pointOfContact}
                            onChange={(e) => handleDetailsFieldChange('pointOfContact', e.target.value)}
                            placeholder="Primary contact"
                          />
                        </DetailField>
                      </DetailSplit>

                      <DetailField>
                        Business address (optional)
                        <DetailTextarea
                          value={detailsForm.businessAddress}
                          onChange={(e) => handleDetailsFieldChange('businessAddress', e.target.value)}
                          placeholder="Business address"
                        />
                      </DetailField>

                      <DetailField>
                        Notes (optional)
                        <DetailTextarea
                          value={detailsForm.bio}
                          onChange={(e) => handleDetailsFieldChange('bio', e.target.value)}
                          placeholder="Internal candidate notes"
                        />
                      </DetailField>

                      <InlineActions>
                        {updateUserBusinessDetails.isError ? (
                          <InlineMeta>Failed to save details.</InlineMeta>
                        ) : detailsSavedAt ? (
                          <InlineMeta>Saved</InlineMeta>
                        ) : !detailsForm.firstName.trim() ||
                          !detailsForm.lastName.trim() ||
                          !detailsForm.userType.trim() ? (
                          <InlineMeta>First name, last name, and customer type are required.</InlineMeta>
                        ) : null}
                        <SecondaryButton
                          type="button"
                          disabled={!detailsFormDirty || isSavingDetails || !canEditClientDetails}
                          onClick={handleDetailsReset}
                        >
                          Reset
                        </SecondaryButton>
                        <ActionButton
                          type="submit"
                          disabled={
                            !detailsFormDirty ||
                            isSavingDetails ||
                            !canEditClientDetails ||
                            !detailsForm.firstName.trim() ||
                            !detailsForm.lastName.trim() ||
                            !detailsForm.userType.trim()
                          }
                        >
                          {isSavingDetails ? 'Saving...' : 'Save details'}
                        </ActionButton>
                      </InlineActions>
                    </DetailForm>
                  </DrawerGreen>
                </>
              ) : detailsRoleIsOperator ? (
                <>
                  <DrawerGray>
                    <DrawerPanelTitle>Operator overview</DrawerPanelTitle>
                    <DrawerText>
                      {detailsRole === 'agent'
                        ? 'Agent details include workload, assigned clients, organization role, and title.'
                        : 'Admin details include workload, assigned clients, and organization role.'}
                    </DrawerText>
                  </DrawerGray>

                  <DetailSplit>
                    <DrawerGreen>
                      <DrawerPanelTitle>Open ticket stats</DrawerPanelTitle>
                      <DrawerText>
                        Total open: {statsTotal}
                        <br />
                        Open: {statsOpen} | In progress: {statsInProgress} | Waiting on client: {statsWaiting}
                      </DrawerText>
                    </DrawerGreen>

                    <DrawerGray>
                      <DrawerPanelTitle>Organization role</DrawerPanelTitle>
                      <DrawerText>
                        Title: {detailsOperatorTitle ?? <MissingValue>--</MissingValue>}
                      </DrawerText>
                      {organizationRoles.length ? (
                        organizationRoles.map((membership) => (
                          <ListItem key={`${membership.orgId}-${membership.role}`}>
                            <strong>{membership.orgName}</strong>
                            <MetaLine>{membership.role}</MetaLine>
                          </ListItem>
                        ))
                      ) : (
                        <HelperText>No organization role found.</HelperText>
                      )}
                    </DrawerGray>
                  </DetailSplit>

                  <DrawerGreen>
                    <DetailForm onSubmit={handleDetailsSave}>
                      <DrawerPanelTitle>Operator profile</DrawerPanelTitle>
                      <DrawerText>
                        Update the operator title here. Team assignment is managed in the Teams tab, and
                        supervisor is derived automatically from the Brain assignment chain.
                      </DrawerText>

                      <DetailSplit>
                        <DetailField>
                          Team
                          <DetailInput
                            value={detailsForm.team}
                            readOnly
                            placeholder="Operations"
                          />
                        </DetailField>

                        <DetailField>
                          Title
                          <DetailInput
                            value={detailsForm.title}
                            onChange={(e) => handleDetailsFieldChange('title', e.target.value)}
                            placeholder="Role title"
                          />
                        </DetailField>
                      </DetailSplit>

                      <DetailField>
                        Supervisor
                        <DetailInput
                          value={detailsResolvedSupervisorName ?? ''}
                          readOnly
                          placeholder="Derived from Brain assignment"
                        />
                      </DetailField>

                      <InlineActions>
                        {updateUserBusinessDetails.isError ? (
                          <InlineMeta>Failed to save details.</InlineMeta>
                        ) : detailsSavedAt ? (
                          <InlineMeta>Saved</InlineMeta>
                        ) : null}
                        <SecondaryButton
                          type="button"
                          disabled={!detailsFormDirty || isSavingDetails || !canEditOperatorDetails}
                          onClick={handleDetailsReset}
                        >
                          Reset
                        </SecondaryButton>
                        <ActionButton
                          type="submit"
                          disabled={!detailsFormDirty || isSavingDetails || !canEditOperatorDetails}
                        >
                          {isSavingDetails ? 'Saving...' : 'Save details'}
                        </ActionButton>
                      </InlineActions>
                    </DetailForm>
                  </DrawerGreen>

                  <DrawerGray>
                    <PanelHeadingRow>
                      <DrawerPanelTitle>Assigned clients</DrawerPanelTitle>
                      {canAdminAssignClientsToOperator ? (
                        <PlusIconButton
                          type="button"
                          aria-label="Assign clients to this agent"
                          onClick={() => setIsAssignClientsModalOpen(true)}
                        >
                          +
                        </PlusIconButton>
                      ) : null}
                    </PanelHeadingRow>
                    <PanelToolbar>
                      <PanelSearchInput
                        value={assignedClientsQuery}
                        onChange={(e) => setAssignedClientsQuery(e.target.value)}
                        placeholder="Search assigned clients by name or business..."
                      />
                      <PanelCount>{groupedAssignedClients.length} clients</PanelCount>
                    </PanelToolbar>

                    {groupedAssignedClients.length ? (
                      <ScrollList>
                        {filteredAssignedClients.length ? (
                          filteredAssignedClients.map((client) => (
                            <ListItem key={`assigned-group-${client.key}`}>
                              <strong>
                                {client.businessName ?? <MissingValue>--</MissingValue>}
                              </strong>
                              <MetaLine>
                                {client.name || '--'} - {client.email}
                              </MetaLine>
                              {canAssignClientsToSelectedOperator ? (
                                <ActionButton
                                  type="button"
                                  disabled={isUpdatingAssignment}
                                  onClick={() => {
                                    void handleRemoveClientGroupFromOperator(client.clientUids);
                                  }}
                                >
                                  {isUpdatingAssignment ? 'Removing...' : 'Remove from this operator'}
                                </ActionButton>
                              ) : null}
                            </ListItem>
                          ))
                        ) : (
                          <HelperText>No assigned clients match this search.</HelperText>
                        )}
                      </ScrollList>
                    ) : (
                      <HelperText>No clients assigned yet.</HelperText>
                    )}
                  </DrawerGray>

                  {detailsRole === 'agent' ? (
                    <DrawerGray>
                      <DrawerPanelTitle>Agent function / title</DrawerPanelTitle>
                      <ActionLabel>
                        Existing titles
                        <ActionSelect
                          value={agentTitleDraft}
                          onChange={(e) => setAgentTitleDraft(e.target.value)}
                        >
                          <option value="">Select title</option>
                          {availableAgentTitles.map((title) => (
                            <option key={`title-${title}`} value={title}>
                              {title}
                            </option>
                          ))}
                        </ActionSelect>
                      </ActionLabel>
                      <ActionLabel>
                        Add new title
                        <DetailInput
                          value={newAgentTitle}
                          onChange={(e) => setNewAgentTitle(e.target.value)}
                          placeholder="Ex: Product Strategist"
                        />
                      </ActionLabel>
                      <ActionButton
                        type="button"
                        disabled={(!newAgentTitle.trim() && !agentTitleDraft.trim()) || isUpdatingTitle || !viewerIsAdmin}
                        onClick={handleUpdateAgentTitle}
                      >
                        {isUpdatingTitle ? 'Saving title...' : 'Save title'}
                      </ActionButton>
                      {!viewerIsAdmin ? (
                        <HelperText>Only admins can update agent titles.</HelperText>
                      ) : null}
                    </DrawerGray>
                  ) : null}
                </>
              ) : (
                <DrawerGray>
                  <DrawerPanelTitle>Details</DrawerPanelTitle>
                  <DrawerText>No role-specific details available for this user.</DrawerText>
                </DrawerGray>
              )}
            </>
          ) : null}

          {detailsTab === 'tasks' ? (
            <>
              <DrawerGray>
                <DrawerPanelTitle>Progress</DrawerPanelTitle>
                <DrawerText>
                  Next task: {detailsOnboarding?.nextTask || 'No pending task'}
                  <br />
                  Completion: {detailsOnboarding?.completionPercent ?? 0}%
                </DrawerText>
              </DrawerGray>

              {detailsTasks.isLoading ? (
                <DrawerGray>
                  <DrawerText>Loading tasks...</DrawerText>
                </DrawerGray>
              ) : (detailsTasks.data?.tasks ?? []).length === 0 ? (
                <DrawerGray>
                  <DrawerText>No tasks found for this user.</DrawerText>
                </DrawerGray>
              ) : (
                (detailsTasks.data?.tasks ?? []).map((task, index) => {
                  const TaskPanel = index % 2 === 0 ? DrawerGreen : DrawerGray;
                  return (
                    <TaskPanel key={task.id}>
                      <TaskTitle>{task.title}</TaskTitle>
                      {task.description ? <TaskMeta>{task.description}</TaskMeta> : null}
                      <TaskStatus $status={task.status}>{task.status}</TaskStatus>
                    </TaskPanel>
                  );
                })
              )}
            </>
          ) : null}

          {detailsTab === 'projects' ? (
            <DrawerGray>
              <DrawerPanelTitle>Projects</DrawerPanelTitle>
              <DrawerText>Project list UI is ready. Assigned projects and milestone health wire-up is next.</DrawerText>
            </DrawerGray>
          ) : null}

          {detailsTab === 'tickets' ? (
            <DrawerGray>
              <DrawerPanelTitle>Tickets</DrawerPanelTitle>
              <DrawerText>Ticket stream UI is ready. Queue status, SLA urgency, and activity feed will be wired next.</DrawerText>
            </DrawerGray>
          ) : null}

          {detailsTab === 'teams' ? (
            <>
              <DrawerGray>
                <DrawerPanelTitle>Team assignment</DrawerPanelTitle>
                <DrawerText>
                  {assignedTeamNode
                    ? `Current team: ${getBrainNodeLabel(assignedTeamNode)}`
                    : 'No Brain node assignment found for this user.'}
                </DrawerText>
                {assignedTeamPath.length ? (
                  <TeamPathHint>
                    Path: {assignedTeamPath.map((node) => getBrainNodeLabel(node)).join(' > ')}
                  </TeamPathHint>
                ) : null}
              </DrawerGray>

              <DrawerGreen>
                <DrawerPanelTitle>Team builder</DrawerPanelTitle>
                <DrawerText>
                  Select a node path. The final selected node becomes this user&apos;s team.
                </DrawerText>

                {!canManageTeamsForSelectedUser ? (
                  <HelperText>
                    {detailsIsLocalCandidate
                      ? 'Local candidate records cannot be assigned to Brain nodes.'
                      : 'Only admins can assign teams from the Brain hierarchy.'}
                  </HelperText>
                ) : isLoadingTeamBuilder ? (
                  <HelperText>Loading Brain nodes...</HelperText>
                ) : teamBuilderLevels.length === 0 ? (
                  <HelperText>No active Brain nodes found. Create nodes in Brain first.</HelperText>
                ) : (
                  <TeamBuilderStack>
                    {teamBuilderLevels.map((level) => {
                      const parentLabel = level.parentNode ? getBrainNodeLabel(level.parentNode) : null;
                      return (
                        <ActionLabel key={`team-level-${level.levelIndex}`}>
                          {level.levelIndex === 0
                            ? 'Select root node'
                            : `Select child node under ${parentLabel ?? 'parent node'}`}
                          <ActionSelect
                            value={level.selectedNodeId}
                            onChange={(event) => {
                              handleTeamBuilderLevelChange(level.levelIndex, event.target.value);
                            }}
                          >
                            <option value="">
                              {level.levelIndex === 0 ? '-- Select root node --' : '-- Select child node --'}
                            </option>
                            {level.options.map((option) => (
                              <option key={`team-option-${option.id}`} value={option.id}>
                                {getBrainNodeLabel(option)}
                              </option>
                            ))}
                          </ActionSelect>
                        </ActionLabel>
                      );
                    })}
                  </TeamBuilderStack>
                )}

                {selectedTeamPath.length ? (
                  <TeamPathHint>
                    Selected path: {selectedTeamPath.map((node) => getBrainNodeLabel(node)).join(' > ')}
                  </TeamPathHint>
                ) : null}
                {selectedTeamBuilderNode ? (
                  <TeamPathHint>
                    Final team node: <TeamPathBadge>{getBrainNodeLabel(selectedTeamBuilderNode)}</TeamPathBadge>
                    {selectedTeamBuilderLabel ? ` • Team: ${selectedTeamBuilderLabel}` : ' • Not a team node'}
                  </TeamPathHint>
                ) : null}
                {selectedTeamManagerLabel ? (
                  <HelperText>
                    Team manager: {selectedTeamManagerLabel}
                  </HelperText>
                ) : null}
                {teamBuilderError ? <HelperText>{teamBuilderError}</HelperText> : null}
                {teamBuilderMessage ? <HelperText>{teamBuilderMessage}</HelperText> : null}

                <InlineActions>
                  <SecondaryButton
                    type="button"
                    disabled={isLoadingTeamBuilder || isSavingTeamBuilder || !viewerIsAdmin}
                    onClick={() => {
                      void loadTeamBuilderNodes();
                    }}
                  >
                    Refresh nodes
                  </SecondaryButton>
                  <ActionButton
                    type="button"
                    disabled={
                      !canManageTeamsForSelectedUser ||
                      !selectedTeamBuilderNode ||
                      !selectedTeamBuilderLabel ||
                      isSavingTeamBuilder ||
                      isLoadingTeamBuilder
                    }
                    onClick={() => {
                      void handleAssignUserToSelectedTeam();
                    }}
                  >
                    {isSavingTeamBuilder ? 'Assigning...' : 'Assign team'}
                  </ActionButton>
                </InlineActions>
              </DrawerGreen>
            </>
          ) : null}

          {detailsTab === 'actions' && viewerIsAdmin ? (
            <>
              <DrawerGray>
                <DrawerPanelTitle>Role management</DrawerPanelTitle>
                <ActionLabel>
                  Role
                  <ActionSelect
                    value={actionsRole}
                    onChange={(e) => {
                      setActionsRole(e.target.value as AssignableRole);
                      if (roleUpdateError) setRoleUpdateError(null);
                    }}
                  >
                    <option value="admin">Admin</option>
                    <option value="agent">Agent</option>
                    <option value="client">Client</option>
                  </ActionSelect>
                </ActionLabel>
                <ActionButton
                  type="button"
                  disabled={
                    !detailsUid ||
                    actionsRole === detailsRole ||
                    updateRole.isPending ||
                    detailsIsLocalCandidate
                  }
                  onClick={() =>
                    detailsUid &&
                    updateRole.mutate(
                      { uid: detailsUid, role: actionsRole },
                      {
                        onSuccess: () => setRoleUpdateError(null),
                        onError: (error) => {
                          if (isAxiosError(error)) {
                            const payload = error.response?.data as { message?: string | string[] } | undefined;
                            const message = payload?.message;
                            if (Array.isArray(message) && message.length > 0) {
                              setRoleUpdateError(message.join(' '));
                              return;
                            }
                            if (typeof message === 'string' && message.trim().length > 0) {
                              setRoleUpdateError(message);
                              return;
                            }
                          }
                          setRoleUpdateError(
                            error instanceof Error ? error.message : 'Failed to update role.',
                          );
                        },
                      },
                    )
                  }
                >
                  {updateRole.isPending ? 'Saving role...' : 'Apply role change'}
                </ActionButton>
                {roleUpdateError ? <HelperText>{roleUpdateError}</HelperText> : null}
                {detailsIsLocalCandidate ? (
                  <HelperText>
                    This candidate was captured locally from the job board and is not yet mapped to a backend user id.
                  </HelperText>
                ) : null}
              </DrawerGray>

              {detailsRole === 'agent' ? (
                <DrawerGray>
                  <DrawerPanelTitle>Schedule permissions</DrawerPanelTitle>
                  <DrawerText>
                    Choose which meeting types this agent can publish as availability.
                  </DrawerText>
                  <PermissionList>
                    {meetingTypeOptions.map((option) => (
                      <PermissionRow key={`permission-${option.type}`}>
                        <PermissionCheckbox
                          checked={agentMeetingPermissionsDraft.includes(option.type)}
                          onChange={() => toggleMeetingPermissionDraft(option.type)}
                        />
                        <span>{option.label}</span>
                      </PermissionRow>
                    ))}
                  </PermissionList>
                  <ActionButton
                    type="button"
                    disabled={!meetingPermissionsDirty || isUpdatingMeetingPermissions}
                    onClick={handleSaveMeetingPermissions}
                  >
                    {isUpdatingMeetingPermissions
                      ? 'Saving permissions...'
                      : 'Save meeting permissions'}
                  </ActionButton>
                </DrawerGray>
              ) : null}

              <DrawerGray>
                <DrawerPanelTitle>Account controls</DrawerPanelTitle>
                <DrawerText>Current status: {detailsBanned ? 'Banned' : 'Active'}</DrawerText>
                <ActionButton
                  type="button"
                  $danger
                  disabled={!detailsUid || updateBan.isPending}
                  onClick={() => detailsUid && updateBan.mutate({ uid: detailsUid, banned: !detailsBanned })}
                >
                  {updateBan.isPending
                    ? 'Updating status...'
                    : detailsBanned
                      ? 'Unlock account'
                      : 'Lock account (ban sign-in)'}
                </ActionButton>
                <HelperText>
                  Locking (ban) keeps the user record and historical data, but prevents account authentication/sign-in.
                </HelperText>
                <ActionLabel style={{ marginTop: 14 }}>
                  Danger zone
                  <HelperText>
                    Permanent delete removes the user from your database and Firebase Authentication. This action cannot be undone.
                  </HelperText>
                </ActionLabel>
                <ActionLabel>
                  Type DELETE to confirm
                  <DetailInput
                    value={deleteUserConfirmText}
                    onChange={(e) => {
                      setDeleteUserConfirmText(e.target.value);
                      if (deleteUserError) setDeleteUserError(null);
                    }}
                    placeholder="DELETE"
                    aria-label="Type DELETE to confirm user deletion"
                  />
                </ActionLabel>
                <ActionButton
                  type="button"
                  $danger
                  disabled={
                    !detailsUid ||
                    deleteUser.isPending ||
                    deleteUserConfirmText.trim().toUpperCase() !== 'DELETE' ||
                    (Boolean(viewerUid) && detailsUid === viewerUid)
                  }
                  onClick={handleDeleteUser}
                >
                  {deleteUser.isPending ? 'Deleting user...' : 'Delete user'}
                </ActionButton>
                {viewerUid && detailsUid === viewerUid ? (
                  <HelperText>You cannot delete your own account.</HelperText>
                ) : null}
                {deleteUserError ? <HelperText>{deleteUserError}</HelperText> : null}
              </DrawerGray>
            </>
          ) : null}
        </DrawerBody>
      </Drawer>

      <AssignClientsModalBackdrop
        type="button"
        aria-label="Close assign clients modal"
        $open={isAssignClientsModalOpen}
        onClick={() => setIsAssignClientsModalOpen(false)}
      />

      <AssignClientsModal $open={isAssignClientsModalOpen}>
        <AssignClientsModalHeader>
          <div>
            <AssignClientsModalTitle>Assign Platform Clients</AssignClientsModalTitle>
            <AssignClientsModalSub>
              Add clients to {detailsIdentityName}. Search by business or contact name.
            </AssignClientsModalSub>
          </div>
          <CloseButton type="button" onClick={() => setIsAssignClientsModalOpen(false)}>
            Close
          </CloseButton>
        </AssignClientsModalHeader>

        <AssignClientsModalBody>
          {canAdminAssignClientsToOperator ? (
            <>
              <PanelToolbar>
                <PanelSearchInput
                  value={assignPlatformClientsQuery}
                  onChange={(e) => setAssignPlatformClientsQuery(e.target.value)}
                  placeholder="Search all platform clients by name or business..."
                />
                <PanelCount>{groupedAssignableClients.length} available</PanelCount>
              </PanelToolbar>

              {groupedAssignableClients.length ? (
                <ModalScrollList>
                  {filteredAssignableClients.length ? (
                    filteredAssignableClients.map((client) => (
                      <ListItem key={`assignable-modal-${client.key}`}>
                        <strong>
                          {client.businessName ?? <MissingValue>--</MissingValue>}
                        </strong>
                        <MetaLine>
                          {client.name || '--'} - {client.email}
                        </MetaLine>
                        <ActionButton
                          type="button"
                          disabled={isUpdatingAssignment}
                          onClick={() => handleAssignClientToOperator(client.clientUid)}
                        >
                          {isUpdatingAssignment ? 'Assigning...' : 'Assign client'}
                        </ActionButton>
                      </ListItem>
                    ))
                  ) : (
                    <HelperText>No platform clients match this search.</HelperText>
                  )}
                </ModalScrollList>
              ) : (
                <HelperText>No additional platform clients available to assign.</HelperText>
              )}
            </>
          ) : (
            <HelperText>Only admins can assign clients from this modal.</HelperText>
          )}
        </AssignClientsModalBody>
      </AssignClientsModal>

      <InviteUserModalBackdrop
        type="button"
        aria-label="Close invite user modal"
        $open={isCreateUserModalOpen}
        onClick={closeCreateUserModal}
      />
      <InviteUserModal $open={isCreateUserModalOpen}>
        <InviteUserModalHeader>
          <InviteUserModalHeaderBar>
            <InviteUserModalIdentity>
              <InviteUserModalEyebrow>CRM invite</InviteUserModalEyebrow>
              <InviteUserModalTitle>Invite User</InviteUserModalTitle>
              <InviteUserModalSub>Invite a user into CRM.</InviteUserModalSub>
            </InviteUserModalIdentity>
            <InviteUserModalCloseButton
              type="button"
              onClick={closeCreateUserModal}
              aria-label="Close invite user modal"
            >
              X
            </InviteUserModalCloseButton>
          </InviteUserModalHeaderBar>
        </InviteUserModalHeader>
        <InviteUserModalBody>
          <InviteUserModalCard>
            <DrawerPanelTitle>User invite details</DrawerPanelTitle>
            <DetailForm onSubmit={handleCreateUserSubmit}>
              <CreateUserField>
                Email
                <CreateUserInput
                  type="email"
                  value={createUserForm.email}
                  onChange={(e) =>
                    setCreateUserForm((prev) => ({ ...prev, email: e.target.value }))
                  }
                  placeholder="name@company.com"
                />
              </CreateUserField>

              <DetailSplit>
                <CreateUserField>
                  First name
                  <CreateUserInput
                    value={createUserForm.firstName}
                    onChange={(e) =>
                      setCreateUserForm((prev) => ({ ...prev, firstName: e.target.value }))
                    }
                    placeholder="First name"
                  />
                </CreateUserField>

                <CreateUserField>
                  Last name
                  <CreateUserInput
                    value={createUserForm.lastName}
                    onChange={(e) =>
                      setCreateUserForm((prev) => ({ ...prev, lastName: e.target.value }))
                    }
                    placeholder="Last name"
                  />
                </CreateUserField>
              </DetailSplit>

              <DetailSplit>
                <CreateUserField>
                  Role
                  <CreateUserRolePicker ref={createUserRolePickerRef}>
                    <CreateUserRoleButton
                      ref={createUserRoleButtonRef}
                      type="button"
                      $open={isCreateUserRoleMenuOpen}
                      aria-haspopup="listbox"
                      aria-expanded={isCreateUserRoleMenuOpen}
                      onClick={() => {
                        if (!isCreateUserRoleMenuOpen) {
                          syncCreateUserRoleMenuPosition();
                        }
                        setIsCreateUserRoleMenuOpen((prev) => !prev);
                      }}
                    >
                      {createUserRoleLabel}
                    </CreateUserRoleButton>
                    <CreateUserRoleChevron $open={isCreateUserRoleMenuOpen}>v</CreateUserRoleChevron>

                    {isCreateUserRoleMenuOpen ? (
                      createPortal(
                        <CreateUserRoleMenu
                          ref={createUserRoleMenuRef}
                          role="listbox"
                          aria-label="Role selection"
                          $top={createUserRoleMenuPosition.top}
                          $left={createUserRoleMenuPosition.left}
                          $width={createUserRoleMenuPosition.width}
                          $openUp={createUserRoleMenuPosition.openUp}
                        >
                          {CREATE_USER_ROLE_OPTIONS.map((option) => (
                            <CreateUserRoleOption
                              key={option.value}
                              type="button"
                              $selected={createUserForm.role === option.value}
                              onClick={() => {
                                setCreateUserForm((prev) => ({
                                  ...prev,
                                  role: option.value,
                                }));
                                setIsCreateUserRoleMenuOpen(false);
                              }}
                            >
                              {option.label}
                            </CreateUserRoleOption>
                          ))}
                        </CreateUserRoleMenu>,
                        document.body,
                      )
                    ) : null}
                  </CreateUserRolePicker>
                </CreateUserField>

                <CreateUserField>
                  Business name (optional)
                  <CreateUserInput
                    value={createUserForm.businessName}
                    onChange={(e) =>
                      setCreateUserForm((prev) => ({ ...prev, businessName: e.target.value }))
                    }
                    placeholder="Business name"
                  />
                </CreateUserField>
              </DetailSplit>

              {createUserError ? (
                <CreateUserHelperText $error>{createUserError}</CreateUserHelperText>
              ) : null}
              {!createUserError && createUserMessage ? (
                <CreateUserHelperText>{createUserMessage}</CreateUserHelperText>
              ) : null}

              <InlineActions>
                <ActionButton type="submit" disabled={inviteUser.isPending}>
                  {inviteUser.isPending ? 'Sending invite...' : 'Invite user'}
                </ActionButton>
              </InlineActions>
            </DetailForm>
          </InviteUserModalCard>
        </InviteUserModalBody>
      </InviteUserModal>

      <FormModalBackdrop
        type="button"
        aria-label="Close post job modal"
        $open={isPostJobModalOpen}
        onClick={closePostJobModal}
      />
      <FormModal $open={isPostJobModalOpen}>
        <FormModalHeader>
          <div>
            <FormModalTitle>Post Job</FormModalTitle>
            <FormModalSub>
              Publish a role to the public careers page using the standard template.
            </FormModalSub>
          </div>
          <CloseButton type="button" onClick={closePostJobModal}>
            Close
          </CloseButton>
        </FormModalHeader>
        <FormModalBody>
          <DetailForm onSubmit={handlePostJobSubmit}>
            <DetailSplit>
              <DetailField>
                Job title
                <DetailInput
                  value={postJobForm.title}
                  onChange={(e) =>
                    setPostJobForm((prev) => ({ ...prev, title: e.target.value }))
                  }
                  placeholder="Ex: Project Manager"
                />
              </DetailField>

              <DetailField>
                Requisition number
                <DetailInput
                  value={postJobForm.requisitionId}
                  onChange={(e) =>
                    setPostJobForm((prev) => ({
                      ...prev,
                      requisitionId: e.target.value,
                    }))
                  }
                  placeholder="Ex: CYB-PM-004"
                />
              </DetailField>
            </DetailSplit>

            <DetailSplit>
              <DetailField>
                Brain team node
                <ActionSelect
                  value={postJobForm.brainNodeId}
                  onChange={(e) => {
                    const nextBrainNodeId = e.target.value;
                    const nextTeam =
                      jobPostingTeamOptionById.get(nextBrainNodeId)?.teamLabel ?? '';
                    setPostJobForm((prev) => ({
                      ...prev,
                      brainNodeId: nextBrainNodeId,
                      team: nextTeam,
                    }));
                  }}
                >
                  <option value="">
                    {jobPostingTeamOptions.length
                      ? 'Select Brain team node'
                      : 'No active team nodes found'}
                  </option>
                  {jobPostingTeamOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </ActionSelect>
              </DetailField>

              <DetailField>
                Team
                <DetailInput
                  value={postJobForm.team}
                  readOnly
                  placeholder="Select a Brain team node"
                />
              </DetailField>

              <DetailField>
                Location
                <DetailInput
                  value={postJobForm.location}
                  onChange={(e) =>
                    setPostJobForm((prev) => ({ ...prev, location: e.target.value }))
                  }
                  placeholder="Remote - US"
                />
              </DetailField>
            </DetailSplit>

            <DetailSplit>
              <DetailField>
                Employment type
                <ActionSelect
                  value={postJobForm.workType}
                  onChange={(e) =>
                    setPostJobForm((prev) => ({
                      ...prev,
                      workType: e.target.value as PostJobFormState['workType'],
                    }))
                  }
                >
                  <option value="full_time">Full time</option>
                  <option value="part_time">Part time</option>
                  <option value="contract">Contract</option>
                </ActionSelect>
              </DetailField>

              <DetailField>
                Published
                <ActionSelect
                  value={postJobForm.published ? 'yes' : 'no'}
                  onChange={(e) =>
                    setPostJobForm((prev) => ({
                      ...prev,
                      published: e.target.value === 'yes',
                    }))
                  }
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </ActionSelect>
              </DetailField>
            </DetailSplit>

            <DetailField>
              Summary
              <DetailTextarea
                value={postJobForm.summary}
                onChange={(e) =>
                  setPostJobForm((prev) => ({ ...prev, summary: e.target.value }))
                }
                placeholder="Short role summary for job cards."
              />
            </DetailField>

            <DetailField>
              Job description
              <DetailTextarea
                value={postJobForm.description}
                onChange={(e) =>
                  setPostJobForm((prev) => ({ ...prev, description: e.target.value }))
                }
                placeholder="Role overview shown on job detail page."
              />
            </DetailField>

            <DetailField>
              Responsibilities (one per line)
              <DetailTextarea
                value={postJobForm.responsibilities}
                onChange={(e) =>
                  setPostJobForm((prev) => ({
                    ...prev,
                    responsibilities: e.target.value,
                  }))
                }
                placeholder="Plan sprint timelines"
              />
            </DetailField>

            <DetailField>
              Requirements (one per line)
              <DetailTextarea
                value={postJobForm.requirements}
                onChange={(e) =>
                  setPostJobForm((prev) => ({ ...prev, requirements: e.target.value }))
                }
                placeholder="3+ years project management"
              />
            </DetailField>

            {postJobError ? <HelperText>{postJobError}</HelperText> : null}
            {!postJobError && postJobMessage ? <HelperText>{postJobMessage}</HelperText> : null}

            <InlineActions>
              <SecondaryButton type="button" onClick={closePostJobModal}>
                Cancel
              </SecondaryButton>
              <ActionButton type="submit" disabled={postCareerJob.isPending}>
                {postCareerJob.isPending ? 'Posting...' : 'Post job'}
              </ActionButton>
            </InlineActions>
          </DetailForm>
        </FormModalBody>
      </FormModal>
    </>
  );
}

